import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getProject, getClient, listQuotes, createQuote, updateQuote, deleteQuote,
  quotePdfUrl, listPriceCodes, listTemplates, createTemplate, getCompany, openQuotePdf,
  getJoineryPartsDefaults, listAutoSources,
  setProjectActiveTemplate, updateProject, updateProjectAutoMeasureFront,
} from "../lib/api";
import { calcLocal, fmtEUR, ROOM_PRESETS } from "../lib/calculator";
import AutoMeasureZoneBlock from "../components/automeasure/AutoMeasureZoneBlock";
import AutoMeasureDetailsDialog from "../components/automeasure/AutoMeasureDetailsDialog";
import { canonicalizeAddress } from "../lib/addressCanonical";
import { resolveAutoSource, autoSourceLabel, AutoSourcesContext } from "../lib/autoSource";
import { ROOM_DEFAULTS, autoQuantity, autoQuantityForAction, AUTO_KIND_FORMULA, ROOM_TYPES, stairSourceFor } from "../lib/roomDefaults";
import { deriveRoomDefaultsFromTemplate } from "../lib/templateDefaults";
import RawNumInput from "../components/measure/RawNumInput";
import {
  EXTRA_PRESETS, WALL_PRESET, CEILING_PRESET, FLOOR_PRESET, DOOR_PRESET, WINDOW_PRESET,
} from "../lib/measurePresets";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  ArrowLeft, Plus, Trash2, FileDown, Save, FilePlus2, Calculator,
  Sparkles, FileSpreadsheet, Send, ChevronDown, Copy,
  Home, Ruler, Bookmark, Package, Info, X,
  Settings as SettingsIcon, Eye,
} from "lucide-react";
import { toast } from "sonner";

import RoomQuickFlow from "../components/measure/RoomQuickFlow";
import RoomCard from "../components/measure/RoomCard";
import MeasurementRow from "../components/measure/MeasurementRow";
import ExtraRow from "../components/measure/ExtraRow";
import NumF from "../components/measure/NumF";
import AIAssistantPanel from "../components/measure/AIAssistantPanel";
import CoverEmailPanel from "../components/measure/CoverEmailPanel";
import QuoteEmailComposeDialog from "../components/measure/QuoteEmailComposeDialog";
import SyncStatusBadge from "../components/measure/SyncStatusBadge";
import { ExteriorZoneFlow, ExteriorZoneAddButtons } from "../components/measure/ExteriorZoneFlow";
import ExtraPostenCard from "../components/measure/ExtraPostenCard";
import { uploadZonePhoto, deleteZonePhoto, updateZonePhotoLabel } from "../lib/api";


import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const uuid = () => crypto.randomUUID();

const newRoom = (name = "Kamer", room_type = "other") => ({
  id: uuid(), name, notes: "",
  length: 0, width: 0, height: 0, room_type,
});

const newMeasurement = (over = {}) => ({
  id: uuid(),
  room_id: null,
  description: "",
  formula: "wall",
  length: 0, width: 0, height: 0, quantity: 0,
  multiplier: 1,
  coats: 2, multiplier_coats: false,
  deductions: [],
  price_code_id: null,
  unit_price_override: null,
  vat_rate_override: null,
  notes: "",
  auto_qty: false,
  auto_kind: null,
  ...over,
});

const newDeduction = () => ({ id: uuid(), description: "", area: 0 });

const newExtra = (over = {}) => ({
  id: uuid(),
  room_id: null,
  description: "",
  quantity: 1,
  unit: "fixed",
  unit_price: 0,
  vat_rate: 21,
  kind: "extra",              // v6 F11.3
  discount_pct: null,         // v6 F11.3
  ...over,
});

const emptyQuote = {
  title: "Offerte",
  description: "Schilderwerken volgens onderstaande omschrijving.",
  works_included:
    "Voorbereiden ondergrond (ontstoffen, ontvetten), afplakken, plaatselijk plamuren, schuren en tussentijds ontstoffen. Aanbrengen van afwerklagen.",
  remarks: "Prijzen excl. BTW tenzij anders vermeld. Offerte 30 dagen geldig.",
  rooms: [],
  work_zones: [],
  measurements: [],
  extra_lines: [],
  show_unit_prices: false,
  show_quantities: true,
  show_codes: true,
  show_layers: false,
  show_vat_details: true,
  show_vat_per_line: false,  // v6 F12c — per-regel BTW-kolom in PDF (default UIT)
  show_room_breakdown: true,
  show_signature_block: true,
  show_photos: true,
};

// Painting presets imported from lib/measurePresets.js

// v6 F12 — Collapsible orphan measurements card
function OrphanMeasurementsCard({ orphans, codeIndex, onRemove }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="emma-card border-2 border-[#E4B16D] bg-[#FFF8EB]"
      data-testid="orphan-measurements-card"
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="orphan-measurements-toggle"
      >
        <ChevronDown
          className={`w-4 h-4 text-[#7F4124] transition-transform ${open ? "" : "-rotate-90"}`}
        />
        <Package className="w-4 h-4 text-[#7F4124]" />
        <h3 className="font-heading font-semibold text-[#2C2C2A]">
          Niet-toegewezen regels
        </h3>
        <span className="text-xs text-[#7F4124]">
          ({orphans.length}) — tellen mee in het totaal
        </span>
      </button>
      {open && (
        <div className="mt-3">
          <p className="text-xs text-[#4F4F4D] mb-3">
            Deze regels zijn niet aan een kamer of werkzone gekoppeld
            (meestal uit een sjabloon-import). Ze blijven in het totaal
            meetellen tot je ze verwijdert of toewijst.
          </p>
          <div className="space-y-2">
            {orphans.map((m) => {
              const pc = m.price_code_id ? codeIndex[m.price_code_id] : null;
              const qty = +m.quantity || +m.length || 0;
              const price = m.unit_price_override != null
                ? +m.unit_price_override
                : +(pc?.default_price || 0);
              return (
                <div
                  key={m.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border border-[#E4B16D] bg-white"
                  data-testid={`orphan-row-${m.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#2C2C2A] truncate">
                      {m.description || pc?.description || "—"}
                    </div>
                    <div className="text-[10px] text-[#5D7259] font-mono">
                      {pc?.code || m.action_code || "?"}
                      {" · "}{qty} × € {price.toFixed(2)}
                    </div>
                  </div>
                  <button
                    onClick={() => onRemove(m.id)}
                    className="w-9 h-9 rounded-lg hover:bg-red-50 flex items-center justify-center text-[#D9534F] flex-shrink-0"
                    data-testid={`orphan-remove-${m.id}`}
                    aria-label="Verwijder regel"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PaintMeasure() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [quote, setQuote] = useState(emptyQuote);
  const [priceCodes, setPriceCodes] = useState([]);
  const [templates, setTemplates] = useState([]);
  // v6 F15.1 — Parts defaults per JoineryObjectKind (labels + units).
  // Gefetcht in Phase 2; wordt door JoineryBlock → JoineryObjectRow gebruikt
  // om de "Mee opnemen in berekening" chevron correct te vullen.
  const [partsDefaults, setPartsDefaults] = useState({});
  // v6 F16 S4 — Auto-source catalog voor live UI-preview van auto-gekoppelde
  // template-regels (spiegelt backend resolve_auto_source).
  const [autoSources, setAutoSources] = useState({});
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  // v6 F12d — Zone-collapse state gelift zodat "Alles open / dicht"
  // ook voor werkzones werkt, niet enkel kamers.
  const [zoneCollapsed, setZoneCollapsed] = useState({});
  // v6 F12d — Offerte details kaart inklapbaar (session-only, gedefault op open).
  const [offerteDetailsOpen, setOfferteDetailsOpen] = useState(true);
  // v6 F13 §5B — Ruimte- en Werkzone-add kaarten inklapbaar op mobiel.
  // Default: null → wordt na eerste quote-load bepaald op basis van "heeft
  // deze offerte al ruimtes/werkzones?". Lege offerte → open (helpt starten);
  // gevulde offerte → dicht (rustig zicht op de zones/ruimtes zelf).
  // Op desktop (lg+) blijven ze altijd open — de state stuurt enkel de
  // mobiele CSS-collapse aan.
  const [roomAddOpen, setRoomAddOpen] = useState(null);
  const [zoneAddOpen, setZoneAddOpen] = useState(null);
  // v6 F41 — Aparte collapse-state voor het "Werkdomeinen"-blok (schrijnwerk / trapwerken).
  const [workdomainAddOpen, setWorkdomainAddOpen] = useState(null);
  // v6 F13 §5B — Set initial collapse-state exactly ONCE per quote-load.
  // Reset naar null wanneer activeId wijzigt zodat we bij switch van offerte
  // opnieuw evalueren op basis van de nieuwe rooms/work_zones counts.
  useEffect(() => {
    setRoomAddOpen(null);
    setZoneAddOpen(null);
    setWorkdomainAddOpen(null);
  }, [activeId]);
  // Bepaal open/dicht zodra quote geladen is (rooms/zones ingelezen).
  // Lege offerte → open om starten te vergemakkelijken.
  // Al gevulde offerte → dicht om de zones/ruimtes vlot te bereiken.
  useEffect(() => {
    if (roomAddOpen === null && quote) {
      setRoomAddOpen((quote.rooms || []).length === 0);
    }
    if (zoneAddOpen === null && quote) {
      // Buitenzones-blok: open indien nog geen exterior_area zones aanwezig.
      const exteriorCount = (quote.work_zones || []).filter((z) => z.zone_type === "exterior_area").length;
      setZoneAddOpen(exteriorCount === 0);
    }
    if (workdomainAddOpen === null && quote) {
      // Werkdomeinen-blok: open indien nog geen schrijnwerk/trap zones aanwezig.
      const workdomainCount = (quote.work_zones || []).filter((z) => z.zone_type !== "exterior_area").length;
      setWorkdomainAddOpen(workdomainCount === 0);
    }
    // eslint-disable-next-line
  }, [quote?.rooms?.length, quote?.work_zones?.length]);
  const [advancedRooms, setAdvancedRooms] = useState({});
  // v6 F1 — Subtle onboarding tip about auto-save. Shown once per browser
  // (localStorage flag) and re-openable via the Info button in the header.
  const [autoSaveTipVisible, setAutoSaveTipVisible] = useState(false);
  useEffect(() => {
    try {
      const seen = localStorage.getItem("emma:tipSeen:autoSave");
      if (!seen) setAutoSaveTipVisible(true);
    } catch { /* ignore */ }
  }, []);
  const dismissAutoSaveTip = () => {
    setAutoSaveTipVisible(false);
    try { localStorage.setItem("emma:tipSeen:autoSave", "1"); } catch { /* ignore */ }
  };
  const showAutoSaveTipManually = () => setAutoSaveTipVisible(true);
  // v6 F2 — Track which room was most recently added so we can default-open it.
  const [lastAddedRoomId, setLastAddedRoomId] = useState(null);
  // v6 F42k Correctieronde v10 P1 — Auto-scroll naar nieuw aangemaakte zone.
  // Gebruikers meldden dat "+ Woonruimte" onbedoeld leek te falen omdat de
  // nieuwe zone buiten beeld werd toegevoegd. Deze effect scrollt smooth
  // naar de nieuwe rij zodra hij in de DOM verschijnt, klapt hem open en
  // toont een korte highlight-flash.
  useEffect(() => {
    if (!lastAddedRoomId) return;
    // Wacht 1 render-tick zodat de zone in de DOM staat.
    const t = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-zone-anchor="${lastAddedRoomId}"]`);
      if (!el) return;
      try {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch { el.scrollIntoView(); }
      // Kort highlight-effect via CSS-klasse
      el.classList.add("emma-zone-highlight");
      setTimeout(() => el.classList.remove("emma-zone-highlight"), 1600);
    });
    return () => cancelAnimationFrame(t);
  }, [lastAddedRoomId]);
  // dirty tracker
  const [dirty, setDirty] = useState(false);

  // online/offline listener
  useEffect(() => {
    const on = () => { setIsOnline(true); setSyncStatus(dirty ? "local" : "synced"); };
    const off = () => { setIsOnline(false); setSyncStatus("offline"); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, [dirty]);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", description: "" });
  const [confirmRoomDel, setConfirmRoomDel] = useState(null);
  // v6 E8 — Pending room template change (shows confirm dialog when room has measurements).
  // Shape: { room, newTemplateId } or null. newTemplateId can be "" to clear.
  const [pendingRoomTplChange, setPendingRoomTplChange] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [pdfResult, setPdfResult] = useState(null);
  const [quickCalcRoom, setQuickCalcRoom] = useState(null);
  const [quickCalcForm, setQuickCalcForm] = useState({ length: 5, width: 4, height: 2.5, walls: true, ceiling: true });
  const [syncStatus, setSyncStatus] = useState("synced");
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [company, setCompany] = useState({});
  // v14ao — AutoMeasure werkt nu op zone-niveau; dialog-state uit v14am verwijderd.

  const codeIndex = useMemo(() => Object.fromEntries(priceCodes.map((p) => [p.id, p])), [priceCodes]);
  const codeByCode = useMemo(() => Object.fromEntries(priceCodes.map((p) => [p.code, p])), [priceCodes]);
  const codeByDesc = useMemo(() => Object.fromEntries(priceCodes.map((p) => [p.description, p])), [priceCodes]);

  // v6 E1 — Resolve active template objects (interior + exterior) from project.
  // Used to derive room/zone defaults instead of the global ROOM_DEFAULTS.
  const activeInteriorTemplate = useMemo(() => {
    const id = project?.active_template_interior_id;
    return id ? templates.find((t) => t.id === id) || null : null;
  }, [project?.active_template_interior_id, templates]);
  const activeExteriorTemplate = useMemo(() => {
    const id = project?.active_template_exterior_id;
    return id ? templates.find((t) => t.id === id) || null : null;
  }, [project?.active_template_exterior_id, templates]);
  // v6 F11.7 — Template-mapping bug fix: detecteer missing-IDs zodat we
  // "Template niet gevonden" kunnen tonen i.p.v. stilletjes terug te vallen.
  const interiorTemplateMissing = useMemo(() => {
    const id = project?.active_template_interior_id;
    return !!id && templates.length > 0 && !templates.find((t) => t.id === id);
  }, [project?.active_template_interior_id, templates]);
  const exteriorTemplateMissing = useMemo(() => {
    const id = project?.active_template_exterior_id;
    return !!id && templates.length > 0 && !templates.find((t) => t.id === id);
  }, [project?.active_template_exterior_id, templates]);
  const interiorActions = useMemo(
    () => deriveRoomDefaultsFromTemplate(activeInteriorTemplate, priceCodes),
    [activeInteriorTemplate, priceCodes],
  );

  // v6 E8 — Per-room template override.
  // Categories suitable for interior rooms (incl. wallpaper/decor/lacquer).
  // v6 F42k Correctieronde v4 — Whitelist uitgebreid met "Binnenschilderwerk"
  // zodat de nieuwe spuit-template "EMMA™ — Wanden en plafonds spuiten"
  // beschikbaar wordt in de room-picker. "Binnenschrijnwerk" blijft bewust
  // uitgesloten (Schrijnwerk spuiten hoort in de schrijnwerk-flow).
  // 'general' blijft geëxcludeerd — geen verzamelbak voor irrelevante templates.
  const ROOM_TEMPLATE_CATEGORIES = useMemo(
    () => new Set(["interior", "lacquer", "decorative", "wallpaper", "Binnenschilderwerk"]),
    [],
  );
  // v6 F42k Correctieronde v3 — Werkzone-specifieke template_context-waarden
  // die NOOIT in de room-picker mogen verschijnen. Deze templates horen
  // uitsluitend thuis in hun eigen zone-flow:
  //   • extras              → "Meerwerken" bloc onderaan de offerte
  //   • stairs              → Werkzone → Trap
  //   • interior_joinery*   → Werkzone → Binnenschrijnwerk
  //   • exterior_joinery*   → Werkzone → Buitenschrijnwerk
  //   • facade_*/wood_exterior → Werkzone → Buitenzone
  const EXCLUDED_ROOM_TEMPLATE_CONTEXTS = useMemo(
    () => new Set([
      "extras",
      "stairs",
      "interior_joinery",
      "interior_joinery_deco",
      "exterior_joinery",
      "exterior_joinery_deco",
      "facade_paint",
      "facade_limewash",
      "wood_exterior",
    ]),
    [],
  );
  const roomTemplateOptions = useMemo(
    () => templates.filter((t) => {
      if (t.active === false) return false;
      // v6 F42k Correctieronde v3 — strikte exclude op werkzone-contexts.
      if (t.template_context && EXCLUDED_ROOM_TEMPLATE_CONTEXTS.has(t.template_context)) return false;
      // v6 F11.8 — Ondersteun multi-category. Toon template als ≥1 van zijn
      // categories overeenstemt met een interieur-relevante categorie.
      const cats = (t.categories && t.categories.length) ? t.categories : [t.category];
      return cats.some((c) => ROOM_TEMPLATE_CATEGORIES.has(c));
    }),
    [templates, ROOM_TEMPLATE_CATEGORIES, EXCLUDED_ROOM_TEMPLATE_CONTEXTS],
  );
  // For a given room, derive the effective default actions:
  //   1. If room has an override template_id → use it
  //   2. Else fall back to project's active interior template
  //   3. Else fall back to ROOM_DEFAULTS[room_type]
  const getRoomActions = useCallback((room) => {
    if (room?.template_id) {
      const tpl = templates.find((t) => t.id === room.template_id);
      const derived = deriveRoomDefaultsFromTemplate(tpl, priceCodes);
      if (derived) return derived;
    }
    // v6 F9.2/F9.3 — Trap- of Algemeen-zones gebruiken nooit de project-
    // binnen-template (S-codes voor muren/plafonds zijn niet relevant). Toon
    // enkel acties die expliciet via een room-template (template_id) gekozen
    // zijn, anders leeg.
    if (room?.room_type === "stair") return ROOM_DEFAULTS.stair || [];
    if (room?.room_type === "general") return ROOM_DEFAULTS.general || [];
    // v6 F34.1 — Geen legacy fallback meer naar ROOM_DEFAULTS[room_type]
    // (bevatte "Deuren lakken/Ramen lakken/Afplakken"). Als er noch een room-
    // template noch een project-interior-template actief is, retourneer een
    // lege lijst i.p.v. verouderde regels.
    return interiorActions || [];
  }, [templates, priceCodes, interiorActions]);

  // Self-heal pass: when priceCodes load AFTER measurements were created (which
  // can happen if the user taps a room-type button before the API resolves),
  // some measurements end up with price_code_id=null. Once codes are available,
  // back-fill the id by matching on action_code (stable hint) first, then on
  // description as a loose fallback. Idempotent; runs whenever priceCodes
  // change. Does NOT mark the quote dirty — but the next manual or auto-save
  // will persist the healed state.
  useEffect(() => {
    if (!priceCodes.length) return;
    setQuote((q) => {
      if (!q.measurements?.length) return q;
      let changed = false;
      const newM = q.measurements.map((m) => {
        if (m.price_code_id) return m;
        const pc = (m.action_code && codeByCode[m.action_code]) ||
                   (m.description && codeByDesc[m.description]) || null;
        if (!pc) return m;
        changed = true;
        return { ...m, price_code_id: pc.id };
      });
      return changed ? { ...q, measurements: newM } : q;
    });
  }, [priceCodes, codeByCode, codeByDesc]);

  // v6 H — sessionStorage cache for instant render on back-navigation.
  // Keys: `emma:pm:project:<projectId>` (project+client+codes+templates+quotes index)
  //       `emma:pm:quote:<quoteId>` (the active quote payload)
  // Strategy: read-through (render cache immediately, then merge fresh server data).
  const PM_CACHE_KEY = `emma:pm:project:${projectId}`;
  const cacheRead = () => {
    try {
      const raw = sessionStorage.getItem(PM_CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const cacheWrite = useCallback((patch) => {
    try {
      const cur = cacheRead() || {};
      sessionStorage.setItem(PM_CACHE_KEY, JSON.stringify({ ...cur, ...patch }));
    } catch { /* quota / unavailable — silent */ }
  }, [PM_CACHE_KEY]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Phase 1: render from sessionStorage cache instantly (no spinner / blank).
    const cached = cacheRead();
    if (cached) {
      if (cached.project) setProject(cached.project);
      if (cached.client) setClient(cached.client);
      if (cached.priceCodes?.length) setPriceCodes(cached.priceCodes);
      if (cached.templates?.length) setTemplates(cached.templates);
      if (cached.company) setCompany(cached.company);
      if (cached.quotes?.length) {
        setQuotes(cached.quotes);
        if (cached.activeId) setActiveId(cached.activeId);
        const active = cached.quotes.find((x) => x.id === cached.activeId) || cached.quotes[0];
        if (active) setQuote({ ...emptyQuote, ...active });
      }
    }
    // Phase 2: refresh from server in the background and merge in.
    (async () => {
      const [codesR, tplsR, compR, partsR, autoR] = await Promise.allSettled([
        listPriceCodes({ active_only: true }),
        // v6 F16.2 — Laad ALLE sjablonen (incl. legacy inactieve) zodat bestaande
        // zones met een verouderde template_id de "verouderd"-waarschuwing kunnen
        // tonen i.p.v. een rode "Template niet gevonden"-melding. Actieve filter
        // gebeurt in memory via `t.active !== false` waar nodig.
        listTemplates({ active_only: false }),
        getCompany(),
        getJoineryPartsDefaults(),
        listAutoSources(),
      ]);
      let codes, tpls, comp;
      if (codesR.status === "fulfilled") { codes = codesR.value || []; setPriceCodes(codes); }
      else { console.error("listPriceCodes failed", codesR.reason); if (!cached?.priceCodes?.length) toast.error("Prijscodes konden niet geladen worden"); }
      if (tplsR.status === "fulfilled") { tpls = tplsR.value || []; setTemplates(tpls); }
      if (compR.status === "fulfilled") { comp = compR.value || {}; setCompany(comp); }
      if (partsR.status === "fulfilled") { setPartsDefaults(partsR.value || {}); }
      else { console.error("getJoineryPartsDefaults failed", partsR.reason); }
      // v6 F16 S4 — auto-source catalog (statisch, non-blocking bij fout)
      if (autoR.status === "fulfilled") { setAutoSources(autoR.value || {}); }

      try {
        const p = await getProject(projectId);
        setProject(p);
        let cli = null;
        if (p.client_id) {
          try { cli = await getClient(p.client_id); setClient(cli); } catch (e) { console.error("getClient failed", e); }
        }
        const qs = await listQuotes(projectId);
        setQuotes(qs);
        let activeQuoteId = null;
        if (qs[0]) {
          activeQuoteId = qs[0].id;
          // Don't clobber local quote edits if user already changed something
          // during the cache-load phase (the dirty flag protects in-progress work).
          setActiveId(qs[0].id);
          setDirty((curDirty) => {
            if (!curDirty) setQuote({ ...emptyQuote, ...qs[0] });
            return curDirty;
          });
        } else {
          setDirty((curDirty) => {
            if (!curDirty) setQuote({ ...emptyQuote, rooms: [] });
            return curDirty;
          });
        }
        // Persist a snapshot for next visit
        cacheWrite({
          project: p, client: cli, priceCodes: codes, templates: tpls,
          company: comp, quotes: qs, activeId: activeQuoteId,
        });
      } catch (e) {
        console.error("Project load failed", e);
        if (!cached?.project) toast.error("Project kon niet geladen worden — controleer de URL");
      }
    })();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // v6 F16 S4 — Ctx voor calcLocal: injecteer resolver + catalog zodat
  // measurementQty auto_source live kan oplossen (Trap-model in schrijnwerk).
  const autoSourcesCtx = useMemo(
    () => ({ autoSources, partsDefaults, resolveAutoSource }),
    [autoSources, partsDefaults]
  );
  const calc = useMemo(
    () => calcLocal(quote, codeIndex, project?.default_vat_rate, autoSourcesCtx),
    [quote, codeIndex, project?.default_vat_rate, autoSourcesCtx]
  );

  // v6 F29.1 — One-shot fill: alleen wanneer `works_included` LEEG is én de
  // eerste (of huidige) hoofdsjabloon een default_quote_description heeft.
  // Bij verdere sjabloonwissels of extra zones doen we NIETS meer om dubbele
  // teksten te voorkomen. User kan zelf via de knop "Sjabloontekst invoegen"
  // handmatig aanvullen.
  useEffect(() => {
    if (quote.works_included_locked) return;
    if ((quote.works_included || "").trim().length > 0) return;
    if (!templates || templates.length === 0) return;
    const firstTplId = (quote.work_zones || [])
      .map((z) => z.joinery_template_id)
      .find((id) => !!id);
    if (!firstTplId) return;
    const tpl = templates.find((t) => t.id === firstTplId);
    const text = ((tpl || {}).default_quote_description || "").trim();
    if (!text) return;
    setQuote((q) => ({ ...q, works_included: text }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.work_zones, quote.works_included_locked, templates]);

  // v6 F5 — Bug-fix autosave race condition.
  // Probleem: een silent autosave nam tijdens de roundtrip (PUT → response,
  // ~200-500ms) een snapshot van de quote-state. Wijzigingen die in dat
  // venster gebeurden, werden niet gesaved en raakten daarna setDirty(false),
  // waardoor ze nooit meer in een autosave terechtkwamen. Een refresh toonde
  // dan de oude data en gaf de indruk dat een verwijderde regel "terugkwam".
  //
  // Fix: een monotone edit-counter wordt opgebumpt bij elke setQuoteDirty/
  // setField/etc. Bij start van save() snapshotten we de counter, en
  // setDirty(false) gebeurt enkel als er tussen start en end géén edits
  // waren. Anders blijft dirty=true en triggert autosave opnieuw.
  const editRevRef = useRef(0);
  const bumpEdit = () => { editRevRef.current += 1; };
  const setField = (k, v) => { setQuote((q) => ({ ...q, [k]: v })); bumpEdit(); setDirty(true); };

  // wrap any setQuote call to mark dirty automatically
  const setQuoteDirty = (updater) => {
    setQuote(updater);
    bumpEdit();
    setDirty(true);
  };

  // v6 F42k P1 — Undo-geschiedenis voor Offerte Details tekstvelden.
  // Persistent in `quote.offerte_details_history[field]` (LIFO, max 5).
  // Frontend push bij onBlur wanneer waarde gewijzigd is ten opzichte van
  // de focus-startwaarde; pop bij "Ongedaan maken".
  const UNDO_FIELDS = ["title", "description", "works_included", "remarks"];
  const UNDO_MAX = 5;
  const undoFocusValueRef = useRef({});
  const captureUndoFocus = (field) => {
    undoFocusValueRef.current[field] = quote[field] ?? "";
  };
  const pushUndoHistory = (field, currentValue) => {
    const prev = undoFocusValueRef.current[field];
    if (prev === undefined) return;
    // Alleen pushen als de waarde daadwerkelijk gewijzigd is en de vorige
    // waarde niet identiek is aan wat al bovenaan de stack staat.
    if ((currentValue ?? "") === (prev ?? "")) return;
    setQuoteDirty((q) => {
      const hist = { ...(q.offerte_details_history || {}) };
      const stack = Array.isArray(hist[field]) ? [...hist[field]] : [];
      if (stack[0] === prev) return q;
      stack.unshift(prev);
      hist[field] = stack.slice(0, UNDO_MAX);
      return { ...q, offerte_details_history: hist };
    });
    undoFocusValueRef.current[field] = currentValue ?? "";
  };
  const popUndoHistory = (field) => {
    setQuoteDirty((q) => {
      const hist = { ...(q.offerte_details_history || {}) };
      const stack = Array.isArray(hist[field]) ? [...hist[field]] : [];
      if (stack.length === 0) return q;
      const restored = stack.shift();
      hist[field] = stack;
      const patch = { ...q, offerte_details_history: hist, [field]: restored ?? "" };
      // works_included heeft een aparte lock-flag — behoud consistent gedrag.
      if (field === "works_included") patch.works_included_locked = true;
      return patch;
    });
    undoFocusValueRef.current[field] = undefined;
  };
  const undoCount = (field) => {
    const hist = quote?.offerte_details_history || {};
    return Array.isArray(hist[field]) ? hist[field].length : 0;
  };

  // v14g — AI accept: schrijf veldwaarde weg + push undo-entry zodat
  // gebruiker AI-invoegen kan terugdraaien via bestaand ↶-mechanisme.
  const acceptAIField = (field, value) => {
    const prev = quote?.[field] ?? "";
    const next = value ?? "";
    if (prev === next) return;
    setQuoteDirty((q) => {
      const hist = { ...(q.offerte_details_history || {}) };
      const stack = Array.isArray(hist[field]) ? [...hist[field]] : [];
      if (stack[0] !== prev) {
        stack.unshift(prev);
        hist[field] = stack.slice(0, UNDO_MAX);
      }
      const patch = { ...q, offerte_details_history: hist, [field]: next };
      if (field === "works_included") patch.works_included_locked = true;
      return patch;
    });
    undoFocusValueRef.current[field] = next;
  };

  // v14g — Optioneel: projectnaam bijwerken bij AI-titel accept (na
  // expliciete user-confirm via window.confirm in AIAssistantPanel).
  // PUT /api/projects/{id} vereist volledig payload — merge daarom bestaand
  // project-object met de nieuwe naam vóór de call.
  const updateProjectNameFromAI = async (newName) => {
    if (!project?.id || !newName) return;
    try {
      const payload = {
        name: newName,
        client_id: project.client_id,
        description: project.description || "",
        default_vat_rate: project.default_vat_rate ?? 21,
        default_currency: project.default_currency || "EUR",
        default_template_id: project.default_template_id || null,
      };
      const updated = await updateProject(project.id, payload);
      setProject(updated);
    } catch (e) {
      toast.error("Kon projectnaam niet bijwerken");
    }
  };

  // v14g — AI stale-detection fingerprint. Wanneer deze wijzigt na een
  // AI-generatie toont AIAssistantPanel een subtiele notice om opnieuw te
  // genereren (nooit automatisch). Compact: enkel structurele markers, geen
  // hele quote-dump. Wijzigingen aan tekstvelden zelf tellen ook mee zodat
  // handmatige edits óók een fresh-generate triggeren.
  const aiChangeFingerprint = useMemo(() => {
    const q = quote || {};
    const t = (q.title || "") + "|" + (q.description || "") + "|" + (q.works_included || "") + "|" + (q.remarks || "");
    const rc = (q.rooms || []).length;
    const zc = (q.work_zones || []).length;
    const oc = (q.work_zones || []).reduce((s, z) => s + ((z.joinery_objects || []).length + (z.joinery_works || []).length), 0);
    const mc = (q.measurements || []).filter((m) => m && m.price_code_id).length;
    const ec = (q.extra_lines || []).length;
    return `t=${t.length}|r=${rc}|z=${zc}|o=${oc}|m=${mc}|e=${ec}|h=${t}`;
  }, [quote?.title, quote?.description, quote?.works_included, quote?.remarks,
      quote?.rooms, quote?.work_zones, quote?.measurements, quote?.extra_lines]);

  // Rooms
  const addRoom = (room_type = "other", name = null) => {
    const rt = ROOM_TYPES.find((t) => t.key === room_type);
    // v6 F34.1 — Nieuwe gewone kamer krijgt onmiddellijk de project-active
    // interior template als `room.template_id` mee, zodat de room-template-
    // dropdown niet "— Geen sjabloon —" toont bij aanmaak. Speciale zones
    // (stair/general) volgen hun eigen template-keuze.
    const isSpecialZone = room_type === "stair" || room_type === "general";
    const initialTplId = isSpecialZone
      ? null
      : (project?.active_template_interior_id || null);
    const r = { ...newRoom(name || rt?.label || "Nieuwe kamer", room_type), template_id: initialTplId };
    // v6 E1 — sticky template context: when an interior template is active,
    // derive room defaults from its template_lines instead of the global
    // ROOM_DEFAULTS (which is hardcoded to standard painting actions).
    // Fallback to ROOM_DEFAULTS only when no active template is set.
    // v6 F9.2/F9.3 — Trap- en Algemeen-zones zijn fundamenteel anders dan
    // gewone kamers (geen muren/plafonds/L×B×H). De project-template voor
    // binnenschilderwerk mag niet automatisch gezaaid worden. De gebruiker
    // kiest een trap- of algemeen-template via de room-template dropdown.
    // v6 F34.1 — Wanneer we een template_id op de kamer zetten, gebruiken we
    // de derived-actions van precies dat sjabloon (via templates array). Voor
    // speciale zones blijft ROOM_DEFAULTS[room_type] leeg als bron. Wanneer
    // er GEEN template beschikbaar is: lege lijst i.p.v. legacy ROOM_DEFAULTS.
    let baseDefaults = [];
    if (isSpecialZone) {
      baseDefaults = ROOM_DEFAULTS[room_type] || [];
    } else if (initialTplId) {
      const tpl = templates.find((t) => t.id === initialTplId);
      baseDefaults = deriveRoomDefaultsFromTemplate(tpl, priceCodes) || interiorActions || [];
    } else {
      baseDefaults = interiorActions || [];
    }
    const defaults = baseDefaults.filter((d) => d.default_checked);
    const newMs = defaults.map((d) => buildAutoMeasurement(r, d));
    setQuoteDirty((q) => ({
      ...q,
      rooms: [...q.rooms, r],
      measurements: [...q.measurements, ...newMs],
    }));
    setLastAddedRoomId(r.id);
    // New room is open; persist that immediately so a refresh keeps the state.
    setCollapsed((s) => {
      const next = { ...s, [r.id]: false };
      try {
        if (quote?.id) localStorage.setItem(`emma:roomCollapsed:${quote.id}`, JSON.stringify(next));
      } catch (e) { /* ignore */ }
      return next;
    });
  };
  // Build a measurement from a room + default action (auto-linked)
  const buildAutoMeasurement = (room, action) => {
    const pc = codeByCode[action.code];
    // v6 E3 — apply wall deductions only when this price code opts in.
    const applyDed = !!pc?.apply_wall_deductions;
    // v6 F9.2 — stair-zone: pull auto-qty from Room.stair_props for trap-codes.
    const qty = autoQuantityForAction(action, room, applyDed);
    const base = {
      room_id: room.id,
      formula: AUTO_KIND_FORMULA[action.auto_kind] || "custom",
      description: action.label,
      price_code_id: pc?.id || null,
      action_code: action.code,  // v5.3 stable cross-ref for self-heal
      auto_qty: true,
      auto_kind: action.auto_kind,
    };
    if (action.auto_kind === "ceiling" || action.auto_kind === "floor") {
      // v6 F10.2/F10.4 — In free-mode is de plafond/vloer-oppervlakte de som
      // van ceiling_segments — NIET de bounding-box L×W. We slaan dan de
      // berekende quantity op en zetten L/W expliciet op 0 zodat de backend
      // (`_measurement_quantity` formula="ceiling"/"floor") ook fallt terug
      // op `quantity` i.p.v. een verkeerde L×W berekening te maken.
      const isFreeRoom = room?.measure_method === "free";
      if (isFreeRoom) {
        base.length = 0;
        base.width = 0;
        base.quantity = qty;
      } else {
        base.length = +room.length || 0;
        base.width = +room.width || 0;
      }
    } else if (action.auto_kind === "walls") {
      base.quantity = qty;  // formula='custom' uses quantity
    } else if (action.auto_kind === "plinten") {
      base.length = qty;    // formula='linear' uses length
    } else if (action.auto_kind === "fixed") {
      base.quantity = qty || 1;
    } else if (action.auto_kind === "piece") {
      // v6 F9.2 — pre-fill from stair_props (tread_count / spindle_count) when applicable.
      base.quantity = qty;
    } else if (action.auto_kind === "linear") {
      // v6 F9.1/F9.2 — formula='linear' gebruikt length; stair-zone vult lm leuning/balustrade in.
      base.length = qty;
    } else if (action.auto_kind === "hour" || action.auto_kind === "manual") {
      // v6 F9.1/F9.2 — formula='custom' (hour/object-m²): stair-zone vult m² overloop/onderzijde in.
      base.quantity = qty;
    }
    return newMeasurement(base);
  };
  // Re-sync all auto_qty measurements when room L/B/H or deductions change.
  // v6 E3 — for "walls" auto_kind, the apply_wall_deductions flag is read
  // from the bound price_code so codes like "Plafonds schilderen" stay gross
  // while "Muren schilderen" gets net.
  // v6 F9.2 — for stair-zones (room_type='stair'), re-sync also picks up
  // changes to Room.stair_props (tread_count / handrail_lm / balustrade_lm /
  // spindle_count / landing_m2 / underside_m2) and updates auto-qty for the
  // matching trap-code measurements (T-01.*, T-02.*, T-03.04/05, T-04.*).
  const updateRoomDimensions = (id, patch) => {
    setQuoteDirty((q) => {
      const newRooms = q.rooms.map((r) => (r.id === id ? { ...r, ...patch } : r));
      const room = newRooms.find((r) => r.id === id);
      const isStairRoom = room?.room_type === "stair";
      const newMeasurements = q.measurements.map((m) => {
        if (m.room_id !== id || !m.auto_qty) return m;
        // v6 F9.2 — stair-zone: route auto-fill through stair_props per code.
        if (isStairRoom) {
          const stairSrc = stairSourceFor(m.action_code);
          if (!stairSrc) return m;  // not a stair-code → leave manual
          const v = +(room?.stair_props?.[stairSrc]) || 0;
          const kind = m.auto_kind;
          if (kind === "piece" || kind === "fixed" || kind === "hour" || kind === "manual" || kind === "walls") {
            return { ...m, quantity: v };
          }
          if (kind === "linear" || kind === "plinten") {
            return { ...m, length: v };
          }
          return m;
        }
        if (m.auto_kind === "piece") return m;
        const k = m.auto_kind;
        const isFreeRoom = room?.measure_method === "free";
        if (k === "ceiling" || k === "floor") {
          // v6 F7  — bij vrije opmeting komt ceiling-qty uit ceiling_segments.
          // v6 F10.2 — In free-mode MOETEN L/W naar 0 zodat de backend
          //   `_measurement_quantity` niet de bounding-box L×W gebruikt.
          // v6 F10.4 — Zelfde behandeling voor floor: vloer = som van
          //   ceiling-segments (zelfde footprint). L-vormige kamers met
          //   bounding-box L×W kreeg vroeger een te grote vloeroppervlakte.
          if ((k === "ceiling" || k === "floor") && isFreeRoom) {
            return { ...m, length: 0, width: 0, quantity: autoQuantity(k, room) };
          }
          return { ...m, length: +room.length || 0, width: +room.width || 0 };
        }
        if (k === "walls") {
          const pc = m.price_code_id ? codeIndex[m.price_code_id] : null;
          const applyDed = !!pc?.apply_wall_deductions;
          return { ...m, quantity: autoQuantity(k, room, applyDed) };
        }
        if (k === "plinten") {
          return { ...m, length: autoQuantity(k, room) };
        }
        return m;
      });
      return { ...q, rooms: newRooms, measurements: newMeasurements };
    });
  };
  // v6 F9.3 — Conservatieve "type trap" suggestie: bij wissel tussen 'open' en
  // 'closed' swap T-02.01 (open trap) ↔ T-02.02 (gesloten trap). Wisselt enkel
  // wanneer de bestaande regel AUTOFILLED is (quantity === stair_props.tread_count)
  // — bij elke manuele aanpassing wordt de wisseling overgeslagen en de
  // gebruiker krijgt een toast-melding.
  const applyStairTypeSwapIfSafe = (q, roomId, oldType, newType) => {
    if (newType === oldType) return q;
    const isOpenClosedSwap =
      (oldType === "open" && newType === "closed") ||
      (oldType === "closed" && newType === "open");
    if (!isOpenClosedSwap) return q;
    const fromCode = newType === "open" ? "T-02.02" : "T-02.01";
    const toCode   = newType === "open" ? "T-02.01" : "T-02.02";
    const fromPc = codeByCode[fromCode];
    const toPc   = codeByCode[toCode];
    if (!fromPc || !toPc) return q;
    const room = q.rooms.find((r) => r.id === roomId);
    if (!room) return q;
    const tread = +room?.stair_props?.tread_count || 0;
    const fromMeas = q.measurements.find((m) => m.room_id === roomId && m.price_code_id === fromPc.id);
    if (!fromMeas) return q; // niets aangevinkt → niets te wisselen
    const fromQty = +fromMeas.quantity || 0;
    if (fromQty !== tread) {
      toast.message(`Type trap gewijzigd, maar ${fromCode} is handmatig aangepast — niet automatisch geswitcht.`);
      return q;
    }
    const toMeas = q.measurements.find((m) => m.room_id === roomId && m.price_code_id === toPc.id);
    if (toMeas) {
      const toQty = +toMeas.quantity || 0;
      if (toQty !== tread) {
        toast.message(`Type trap gewijzigd, maar ${toCode} is handmatig aangepast — niet automatisch geswitcht.`);
        return q;
      }
      // Beide auto-filled → drop "from"
      toast.success(`Trap-type: ${fromCode} verwijderd (${toCode} blijft staan).`);
      return { ...q, measurements: q.measurements.filter((m) => m.id !== fromMeas.id) };
    }
    // Vervang fromMeas door een nieuwe auto-meting op toCode.
    const action = { code: toCode, label: toPc.description, auto_kind: "piece", default_checked: true };
    const newMeas = buildAutoMeasurement(room, action);
    toast.success(`Trap-type aangepast: ${fromCode} → ${toCode}.`);
    return {
      ...q,
      measurements: [...q.measurements.filter((m) => m.id !== fromMeas.id), newMeas],
    };
  };
  const updateRoom = (id, patch) => {
    // Route dimension OR deduction changes through dimension-aware updater so
    // walls auto-qty stays in sync (v6 E2 wall-deductions apply to "walls" only).
    // v6 F7 — measure_method, wall_segments en ceiling_segments triggeren
    // ook een re-sync, want autoQuantity leest die in vrije-opmeting modus.
    // v6 F9.2 — stair_props triggert ook re-sync (trap-codes lezen daaruit).
    const reSyncKeys = [
      "length", "width", "height", "deductions",
      "measure_method", "wall_segments", "ceiling_segments",
      "stair_props",
    ];
    // v6 F9.3 — Vang stair_type-wijziging vóór de patch zodat we de oude waarde
    // kennen. React batcht de twee setQuoteDirty-aanroepen in één render.
    const prevRoom = quote.rooms.find((r) => r.id === id);
    const oldStairType = prevRoom?.stair_props?.stair_type;
    const newStairType = patch?.stair_props?.stair_type;
    const stairTypeChanged = oldStairType && newStairType && oldStairType !== newStairType;
    if (Object.keys(patch).some((k) => reSyncKeys.includes(k))) {
      updateRoomDimensions(id, patch);
    } else {
      setQuoteDirty((q) => ({ ...q, rooms: q.rooms.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
    }
    if (stairTypeChanged) {
      setQuoteDirty((q) => applyStairTypeSwapIfSafe(q, id, oldStairType, newStairType));
    }
  };
  // Toggle a default action for a room (add/remove measurement).
  // Match on price_code_id OR (fallback) auto_kind + description so manually
  // added or pre-load measurements also fold back into the checklist UX.
  const toggleRoomAction = (room, action) => {
    const pcId = codeByCode[action.code]?.id;
    const matches = (m) =>
      m.room_id === room.id && (
        (pcId && m.price_code_id === pcId) ||
        (m.description && m.description === action.label)
      );
    const existing = quote.measurements.find(matches);
    const hasMatch = !!existing;
    if (hasMatch) {
      // v6 F42k Correctieronde v7 — Snapshot current override in room.saved_price_overrides
      // vóór verwijdering, zodat re-toggle later de override kan herstellen.
      if (pcId && existing.unit_price_override != null && existing.unit_price_override !== "") {
        const newOverrides = { ...(room.saved_price_overrides || {}) };
        newOverrides[pcId] = +existing.unit_price_override;
        setQuoteDirty((q) => ({
          ...q,
          rooms: q.rooms.map((r) => r.id === room.id ? { ...r, saved_price_overrides: newOverrides } : r),
        }));
      }
      // Remove ALL matching measurements so the toggle is symmetric.
      setQuoteDirty((q) => ({ ...q, measurements: q.measurements.filter((m) => !matches(m)) }));
    } else {
      // v6 F42k Correctieronde v8 — Choice-group exclusiviteit.
      // Bij aanvinken van een action met choice_group: eerst alle andere
      // actieve measurements met dezelfde choice_group uitschakelen (met
      // snapshot van hun override) zodat slechts één alternatief tegelijk
      // actief is. Scope = per room; geen kruisbesmetting tussen ruimtes.
      const cg = action.choice_group;
      if (cg && room.template_id) {
        const tpl = templates.find((t) => t.id === room.template_id);
        const cgPcIds = new Set(
          (tpl?.template_lines || [])
            .filter((ln) => ln.choice_group === cg && ln.price_code_id !== pcId)
            .map((ln) => ln.price_code_id)
        );
        if (cgPcIds.size > 0) {
          const newOverrides = { ...(room.saved_price_overrides || {}) };
          setQuoteDirty((q) => {
            const conflictingMs = q.measurements.filter(
              (m) => m.room_id === room.id && cgPcIds.has(m.price_code_id)
            );
            for (const cm of conflictingMs) {
              if (cm.unit_price_override != null && cm.unit_price_override !== "") {
                newOverrides[cm.price_code_id] = +cm.unit_price_override;
              }
            }
            return {
              ...q,
              rooms: q.rooms.map((r) =>
                r.id === room.id ? { ...r, saved_price_overrides: newOverrides } : r
              ),
              measurements: q.measurements.filter(
                (m) => !(m.room_id === room.id && cgPcIds.has(m.price_code_id))
              ),
            };
          });
        }
      }
      const m = buildAutoMeasurement(room, action);
      // v6 F42k Correctieronde v7 — Herstel eventuele eerder bewaarde override.
      const saved = pcId ? room.saved_price_overrides?.[pcId] : null;
      if (saved != null) m.unit_price_override = +saved;
      setQuoteDirty((q) => ({ ...q, measurements: [...q.measurements, m] }));
    }
  };
  const removeRoom = (id) => {
    setConfirmRoomDel(quote.rooms.find((r) => r.id === id) || null);
  };
  const doRemoveRoom = (id) => {
    setQuoteDirty((q) => ({
      ...q,
      rooms: q.rooms.filter((r) => r.id !== id),
      measurements: q.measurements.filter((m) => m.room_id !== id),
      extra_lines: q.extra_lines.filter((e) => e.room_id !== id),
    }));
    setConfirmRoomDel(null);
    toast.success("Kamer verwijderd");
  };

  // v6 E8 — Per-room template override.
  // requestChangeRoomTemplate: if the room has measurements, show a confirm
  // dialog with two actions: "Alleen checklist wijzigen" (keep metingen)
  // or "Vervangen door standaard" (also rebuild defaults). Otherwise apply
  // the change immediately (and seed defaults if a template is selected).
  const requestChangeRoomTemplate = (room, newTemplateId) => {
    const safeId = newTemplateId || null;
    if (safeId === (room.template_id || null)) return;
    const hasMeasurements = quote.measurements.some((m) => m.room_id === room.id);
    if (hasMeasurements) {
      setPendingRoomTplChange({ room, newTemplateId: safeId });
    } else {
      applyRoomTemplateChange(room.id, safeId, { replaceMeasurements: true });
    }
  };
  // applyRoomTemplateChange:
  //   replaceMeasurements=false → only switch room.template_id (metingen behouden)
  //   replaceMeasurements=true  → remove existing measurements for the room,
  //                                seed new defaults from the new template.
  const applyRoomTemplateChange = (roomId, newTemplateId, { replaceMeasurements }) => {
    setQuoteDirty((q) => {
      const room = q.rooms.find((r) => r.id === roomId);
      if (!room) return q;
      const updatedRoom = { ...room, template_id: newTemplateId };
      const newRooms = q.rooms.map((r) => (r.id === roomId ? updatedRoom : r));
      if (!replaceMeasurements) {
        return { ...q, rooms: newRooms };
      }
      // Replace: drop existing measurements for this room, seed default_checked actions.
      const otherMs = q.measurements.filter((m) => m.room_id !== roomId);
      const newDefaults = getRoomActions(updatedRoom).filter((d) => d.default_checked);
      const seeded = newDefaults.map((d) => buildAutoMeasurement(updatedRoom, d));
      return { ...q, rooms: newRooms, measurements: [...otherMs, ...seeded] };
    });
    setPendingRoomTplChange(null);
    toast.success(newTemplateId ? "Template van kamer aangepast" : "Kamer terug op project-standaard");
  };

  // v6 Fase C — WorkZone helpers (parallel to room helpers above)
  const addZone = (preset) => {
    const zoneType = preset.zone_type || "exterior_area";
    const zoneKind = preset.zone_kind || null; // v6 F42k Fase 3
    const zone = {
      id: uuid(),
      name: preset.name || "Werkzone",
      zone_type: zoneType,
      zone_kind: zoneKind,
      notes: "",
      order: (quote.work_zones || []).length,
      length: 0, width: 0, height: 0, area: 0,
      photos: [],
    };
    // v6 F42b — Nieuwe buitenzones starten meteen in multi-surface flow met
    // Gevelvlak 1. Bestaande legacy zones (facade_surfaces=[]) blijven de
    // legacy weergave gebruiken totdat de user "+ Gevelvlak toevoegen" klikt.
    // v6 F42k Fase 3 — Zelfstandige "Buitenelementen"-zone (zone_kind=
    // "buitenelementen") krijgt GEEN facade_surfaces (geen gevel-metingen),
    // enkel facade_extra_elements met auto-groepering per (kind + material).
    if (zoneType === "exterior_area" && zoneKind !== "buitenelementen") {
      // Gebouw-zones (Woning/Chalet/Bijgebouw/Bedrijfsgebouw) → Gevel 1
      // Legacy vrije naam (Tuinmuur zonder zone_kind) → Gevelvlak 1
      const isBuilding = ["woning", "chalet", "bijgebouw", "bedrijfsgebouw"].includes(zoneKind);
      zone.facade_surfaces = [{
        id: uuid(),
        name: isBuilding ? "Gevel 1" : "Gevelvlak 1",
        order: 0,
        shape_type: "rect",
        dim_length: 0,
        dim_height: 0,
        dim_height2: 0,
        manual_area: 0,
        deductions: [],
        surface_type: null,
      }];
    }
    setQuoteDirty((q) => ({ ...q, work_zones: [...(q.work_zones || []), zone] }));
    // v6 F42k Correctieronde v10 P1 — Trigger auto-scroll naar nieuwe zone.
    setLastAddedRoomId(zone.id);
  };
  const updateZone = (id, patch) => setQuoteDirty((q) => ({
    ...q,
    work_zones: (q.work_zones || []).map((z) => z.id === id ? { ...z, ...patch } : z),
  }));
  const removeZone = (id) => setQuoteDirty((q) => ({
    ...q,
    work_zones: (q.work_zones || []).filter((z) => z.id !== id),
    measurements: q.measurements.filter((m) => m.zone_id !== id),
    extra_lines: q.extra_lines.filter((e) => e.zone_id !== id),
  }));

  // v14al → v14ao — AutoMeasure™ zone-level flow.
  // De AutoMeasureZoneBlock roept `applyMeasurementToZone(zoneId, building, strategy)`.
  // Alle geometry-decisions (rect/trapezoid) komen mee vanuit views[i].geometry_type
  // en gross_area_effective_m2 (backend = source of truth).
  const [autoMeasureDetailsOpen, setAutoMeasureDetailsOpen] = useState(false);
  const [autoMeasureDetailsBuilding, setAutoMeasureDetailsBuilding] = useState(null);

  const buildingIdentityFromResult = (r, nFacades) => ({
    building_id: r.building_id,
    provider: r.provider,
    measurement_address: r.address_resolved,
    front_cardinal: r.front_cardinal,
    front_confidence: r.front_confidence,
    front_method: r.front_method,
    roof_type_guess: r.roof_type_guess,
    footprint_area_m2: r.footprint_area_m2,
    total_gross_exterior_m2: r.total_gross_exterior_m2,
    H1: r.H1, H2: r.H2,
    sources: (r.sources || []).map((s) => ({ dataset: s.dataset, provider: s.provider, version: s.version, vintage: s.vintage, license: s.license })),
    segments: r.segments,
    // v14ar — Bewaar bronobjecten + componenten in de Details-metadata zodat
    // "Bronobjecten" in de Details-dialog terug te vinden zijn. Blijft opt-in
    // (oude metingen zonder deze velden werken gewoon door).
    source_objects: (r.source_objects || []).map((o) => ({
      object_class: o.object_class, feature_id: o.feature_id, type: o.type,
      type_label: o.type_label, is_anchor: o.is_anchor,
      area_m2: o.area_m2, distance_to_anchor_m: o.distance_to_anchor_m,
      touches_anchor: o.touches_anchor,
      classification: o.classification, classification_confidence: o.classification_confidence,
      component_label: o.component_label,
      grb_version: o.grb_version, grb_begindatum: o.grb_begindatum,
    })),
    components: (r.components || []).map((c) => ({
      component_id: c.component_id, label: c.label, role: c.role,
      include_by_default: c.include_by_default,
      member_object_ids: c.member_object_ids,
      footprint_area_m2: c.footprint_area_m2,
      total_gross_exterior_m2: c.total_gross_exterior_m2,
      classification_confidence: c.classification_confidence,
      n_physical_facades: (c.physical_facades || []).filter((f) => !f.is_shared && f.is_paintable_default).length,
    })),
    retrieved_at: new Date().toISOString(),
    n_facades: nFacades,
  });

  // v14ap — Lege placeholder-detectie voor gevelvlakken.
  //
  // Wordt gebruikt op 2 plekken:
  //   1. `applyMeasurementToZone` — placeholder wordt weggefilterd zodat een
  //      leeg initieel "Gevel 1" niet naast de AutoMeasure-gevels blijft
  //      staan (bug: 5 gevels ipv gemeten 4).
  //   2. `hasExistingSurfaces` (render) — bepaalt of we de 3-way confirm
  //      moeten tonen. Een zone met alleen placeholder(s) = "leeg" ⇒ geen
  //      confirm-prompt.
  //
  // Placeholder = ALLE van deze condities:
  //   - default naam ("Gevel" / "Gevel 1" / "Gevel 2" / …) — niet manueel hernoemd
  //   - alle dimensies = 0 (dim_length, dim_height, dim_height2)
  //   - manual_area = 0
  //   - geen deductions
  //   - geen automeasure_source (surface werd niet eerder door AutoMeasure gevuld)
  //   - geen surface_type override
  const isEmptyPlaceholderSurface = (s) => {
    if (!s) return false;
    const rawName = (s.name || "").trim();
    const nameIsDefault = rawName === "" || /^Gevel(\s+\d+)?$/i.test(rawName);
    if (!nameIsDefault) return false;
    const noDim = (+s.dim_length || 0) === 0 && (+s.dim_height || 0) === 0 && (+s.dim_height2 || 0) === 0;
    if (!noDim) return false;
    if ((+s.manual_area || 0) > 0) return false;
    if ((s.deductions || []).length > 0) return false;
    if (s.automeasure_source) return false;
    if (s.surface_type) return false;
    return true;
  };

  const zoneHasMeaningfulSurfaces = (zone) =>
    (zone.facade_surfaces || []).some((s) => !isEmptyPlaceholderSurface(s));

  // v14ar — Ondersteunt zowel de nieuwe Level A `components[].physical_facades`
  // (variabel aantal gevels per component) als de legacy `views[]` payload
  // (voor backward-compat met oude AutoMeasure-metingen op reeds bestaande
  // projecten). `selectedComponentIds` is optioneel: leeg = alle
  // `include_by_default=true` componenten.
  const applyMeasurementToZone = (zoneId, building, strategy = "keep",
                                   selectedComponentIds = null) => {
    if (!zoneId || !building) { toast.error("AutoMeasure fout: geen zone/gebouw."); return; }
    const components = Array.isArray(building.components) ? building.components : [];
    const views = building.views || [];

    const h1 = building.H1?.value_m ?? 0;
    const h2 = building.H2?.value_m ?? 0;
    const h1Conf = building.H1?.confidence;
    const h2Conf = building.H2?.confidence;
    const frontCardinal = building.front_cardinal || "N";

    // Front-labels blijven metadata (§9,§13). Nooit als visible name.
    const CARDS = ["N", "O", "Z", "W"];
    const roleForView = (viewCard) => {
      const fi = CARDS.indexOf(frontCardinal);
      const vi = CARDS.indexOf(viewCard);
      if (fi < 0 || vi < 0) return null;
      return ["Voorgevel", "Rechtergevel", "Achtergevel", "Linkergevel"][(vi - fi + 4) % 4];
    };

    const buildMetadata = (extra = {}) => ({
      provider: building.provider,
      building_id: building.building_id,
      address: building.address_resolved,
      retrieved_at: new Date().toISOString(),
      confidence: { H1: h1Conf, H2: h2Conf },
      front_cardinal: frontCardinal,
      front_confidence: building.front_confidence,
      front_method: building.front_method,
      sources: (building.sources || []).map((s) => ({ dataset: s.dataset, version: s.version, vintage: s.vintage })),
      ...extra,
    });

    // v14ar — Nieuwe primaire path: physical_facades per component.
    // Aantal gevelvlakken volgt de werkelijke geometrie (rechthoek→4,
    // L-vorm→6, uitspringing→6+, U-vorm→8, complex→N). GEEN cardinal
    // aggregatie. §10, §11, §12, §13, §15.
    const createSurfacesFromComponents = (baseOrder) => {
      const wanted = selectedComponentIds && selectedComponentIds.length
        ? new Set(selectedComponentIds)
        : new Set(components.filter((c) => c.include_by_default).map((c) => c.component_id));
      const surfaces = [];
      let counter = baseOrder;
      for (const comp of components) {
        if (!wanted.has(comp.component_id)) continue;
        for (const f of comp.physical_facades || []) {
          // v14at-p3b — §20 gedeelde wand + niet-standaard-schilderbare
          // segmenten (bv. `gvl_lbltype = "fotogrammetrische dakoversteek"`)
          // worden NIET automatisch als PaintMeasure-gevelvlak aangemaakt.
          // Ze blijven volledig behouden in backend/source/provenance.
          if (f.is_shared || !f.is_paintable_default) continue;
          const isTrap = f.geometry_type === "trapezoid";
          counter += 1;
          surfaces.push({
            id: crypto.randomUUID(),
            name: `Gevel ${counter}`,
            order: counter - 1,
            shape_type: isTrap ? "trapezoid" : "rect",
            dim_length: +f.length_m || 0,
            dim_height: (f.height_m ?? h1) || 0,
            dim_height2: isTrap ? (f.height2_m ?? h2) || 0 : 0,
            manual_area: 0,
            deductions: [],
            surface_type: null,
            automeasure_source: buildMetadata({
              component_id: comp.component_id,
              component_label: comp.label,
              component_role: comp.role,
              view_cardinal: f.cardinal,
              view_role: roleForView(f.cardinal),
              orientation_deg: f.orientation_deg,
              geometry_type: f.geometry_type,
              gross_effective_m2: f.effective_area_m2,
              edge_indices: f.edge_indices,
              is_shared: false,
            }),
          });
        }
      }
      return surfaces;
    };

    // Legacy fallback: build surfaces from views[] (backward compat, oude
    // metingen op bestaande projecten). Blijft functioneel identiek aan v14ap.
    const createSurfacesFromViews = (baseOrder) => views
      .filter((v) => (v.length_total_m || 0) > 0)
      .map((v, i) => {
        const isTrapezoid = v.geometry_type === "trapezoid";
        const positional = baseOrder + i + 1;
        return {
          id: crypto.randomUUID(),
          name: `Gevel ${positional}`,
          order: baseOrder + i,
          shape_type: isTrapezoid ? "trapezoid" : "rect",
          dim_length: +v.length_total_m || 0,
          dim_height: h1,
          dim_height2: isTrapezoid ? h2 : 0,
          manual_area: 0,
          deductions: [],
          surface_type: null,
          automeasure_source: buildMetadata({
            view_cardinal: v.cardinal,
            view_label: v.label_default,
            view_role: roleForView(v.cardinal),
            geometry_type: v.geometry_type,
            gross_effective_m2: v.gross_area_effective_m2,
          }),
        };
      });

    const useComponents = components.length > 0;
    if (!useComponents && !views.length) {
      toast.error("AutoMeasure vond geen gevels."); return;
    }

    setQuoteDirty((q) => {
      const zones = [...(q.work_zones || [])];
      const zIdx = zones.findIndex((z) => z.id === zoneId);
      if (zIdx < 0) return q;
      const zone = { ...zones[zIdx] };
      const existing = zone.facade_surfaces || [];
      const meaningfulExisting = existing.filter((s) => !isEmptyPlaceholderSurface(s));
      const baseOrder = strategy === "keep" ? meaningfulExisting.length : 0;
      const newSurfaces = useComponents
        ? createSurfacesFromComponents(baseOrder)
        : createSurfacesFromViews(baseOrder);
      const finalSurfaces = strategy === "keep"
        ? [...meaningfulExisting, ...newSurfaces].map((s, i) => ({ ...s, order: i }))
        : newSurfaces.map((s, i) => ({ ...s, order: i }));
      zone.facade_surfaces = finalSurfaces;
      zone.automeasure_building = buildingIdentityFromResult(building, newSurfaces.length);
      zones[zIdx] = zone;
      return { ...q, work_zones: zones };
    });
  };

  // v14at-p5 — Nieuwe atomic pad: backend heeft facade_surfaces +
  // automeasure_building atomisch geschreven. FE neemt de response-waarden
  // direct over in de lokale quote-state zodat de UI onmiddellijk juist is,
  // zonder afhankelijkheid van de 1500 ms autosave-debounce. Omdat de
  // backend al source-of-truth is, is deze setState puur voor UI-render:
  // de volgende autosave is idempotent (zendt dezelfde data terug).
  const applyServerAuthoredSurfacesToZone = (zoneId, applyResponse) => {
    if (!zoneId || !applyResponse) return;
    const surfaces = Array.isArray(applyResponse.facade_surfaces)
      ? applyResponse.facade_surfaces
      : null;
    const building = applyResponse.automeasure_building || null;
    if (!surfaces) return;
    setQuoteDirty((q) => {
      const zones = [...(q.work_zones || [])];
      const zIdx = zones.findIndex((z) => z.id === zoneId);
      if (zIdx < 0) return q;
      const zone = { ...zones[zIdx] };
      zone.facade_surfaces = surfaces;
      if (building) zone.automeasure_building = building;
      zones[zIdx] = zone;
      return { ...q, work_zones: zones };
    });
  };

  const openAutoMeasureDetails = (zone) => {
    const b = zone?.automeasure_building;
    if (!b) return;
    setAutoMeasureDetailsBuilding(b);
    setAutoMeasureDetailsOpen(true);
  };

  // v14an — Persist voorgevel-oriëntatie op het project (via dedicated endpoint).
  const saveProjectFrontOrientation = async (cardinal) => {
    if (!project?.id) return;
    const updated = await updateProjectAutoMeasureFront(project.id, cardinal);
    setProject((p) => ({ ...(p || {}), ...updated }));
  };
  const addZoneMeasurement = (meas) => setQuoteDirty((q) => ({
    ...q,
    measurements: [...q.measurements, { ...meas, id: meas.id || uuid() }],
  }));
  const uploadPhoto = async (zoneId, dataUrl, label = "before") => {
    if (!activeId || !dataUrl) return;
    try {
      const updated = await uploadZonePhoto(activeId, zoneId, dataUrl, label);
      setQuote((q) => ({ ...q, work_zones: updated.work_zones || q.work_zones }));
      toast.success("Foto opgeladen");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Fout bij uploaden";
      toast.error(msg);
    }
  };
  const removePhoto = async (zoneId, idx) => {
    if (!activeId) return;
    try {
      const updated = await deleteZonePhoto(activeId, zoneId, idx);
      setQuote((q) => ({ ...q, work_zones: updated.work_zones || q.work_zones }));
      toast.success("Foto verwijderd");
    } catch {
      toast.error("Fout bij verwijderen foto");
    }
  };
  const setPhotoLabel = async (zoneId, idx, label) => {
    if (!activeId) return;
    try {
      const updated = await updateZonePhotoLabel(activeId, zoneId, idx, label);
      setQuote((q) => ({ ...q, work_zones: updated.work_zones || q.work_zones }));
    } catch {
      toast.error("Fout bij wijzigen label");
    }
  };

  // Measurements
  const addMeasurement = (room_id, preset) => {
    const pc = preset?.code ? codeByCode[preset.code] : null;
    setQuoteDirty((q) => ({
      ...q,
      measurements: [
        ...q.measurements,
        newMeasurement({
          room_id,
          formula: preset?.formula || "wall",
          price_code_id: pc?.id || null,
          description: pc?.description || preset?.description || preset?.label || "",
          length: preset?.formula === "piece" ? 0 : (preset?.formula === "wall" ? 4 : 0),
          height: preset?.formula === "wall" ? 2.5 : 0,
          width: preset?.formula === "ceiling" || preset?.formula === "floor" ? 0 : 0,
          quantity: preset?.formula === "piece" ? 1 : 0,
        }),
      ],
    }));
  };
  const updM = (id, patch) =>
    setQuoteDirty((q) => ({ ...q, measurements: q.measurements.map((m) => m.id === id ? { ...m, ...patch } : m) }));
  const removeM = (id) =>
    setQuoteDirty((q) => ({ ...q, measurements: q.measurements.filter((m) => m.id !== id) }));
  const duplicateM = (id) => {
    const src = quote.measurements.find((m) => m.id === id);
    if (!src) return;
    setQuoteDirty((q) => ({
      ...q,
      measurements: [
        ...q.measurements,
        { ...src, id: uuid(), deductions: (src.deductions || []).map((d) => ({ ...d, id: uuid() })) },
      ],
    }));
  };

  // Deductions (per measurement)
  const addDeduction = (mid) => updM(mid, {
    deductions: [...((quote.measurements.find((x) => x.id === mid)?.deductions) || []), newDeduction()],
  });
  const updDeduction = (mid, did, patch) => {
    const m = quote.measurements.find((x) => x.id === mid);
    if (!m) return;
    updM(mid, { deductions: m.deductions.map((d) => (d.id === did ? { ...d, ...patch } : d)) });
  };
  const remDeduction = (mid, did) => {
    const m = quote.measurements.find((x) => x.id === mid);
    if (!m) return;
    updM(mid, { deductions: m.deductions.filter((d) => d.id !== did) });
  };

  // Extras (scoped per room, or room_id=null for project-level)
  const addExtra = (room_id, preset) => {
    const pc = preset ? codeByCode[preset.code] : null;
    setQuoteDirty((q) => ({
      ...q,
      extra_lines: [
        ...q.extra_lines,
        newExtra({
          room_id: room_id || null,
          description: pc?.description || preset?.description || preset?.label || "",
          unit: pc?.unit || "fixed",
          unit_price: pc?.default_price || 0,
          vat_rate: pc?.vat_rate || 21,
        }),
      ],
    }));
  };
  const updExtra = (id, patch) =>
    setQuoteDirty((q) => ({ ...q, extra_lines: q.extra_lines.map((e) => e.id === id ? { ...e, ...patch } : e) }));
  const remExtra = (id) =>
    setQuoteDirty((q) => ({ ...q, extra_lines: q.extra_lines.filter((e) => e.id !== id) }));

  // Quote actions
  const newQuote = () => {
    setActiveId(null);
    setQuote({ ...emptyQuote, rooms: [] });
  };
  // v6 F12e — Offerte dupliceren: kopieer alle huidige velden (rooms, zones,
  // measurements, extra_lines, toggles) en maak een nieuwe offerte aan.
  // Vervangt de vroegere "Als sjabloon" — sjablonen blijven exclusief voor
  // kamers/werkzones/extra posten.
  const duplicateQuote = async () => {
    try {
      const src = { ...quote };
      // Strip identifiers zodat backend nieuwe genereert
      const stripId = (o) => { const c = { ...o }; delete c.id; return c; };
      const payload = {
        ...src,
        id: undefined,
        title: `${quote.title || "Offerte"} (kopie)`,
        rooms: (src.rooms || []).map(stripId),
        work_zones: (src.work_zones || []).map(stripId),
        measurements: (src.measurements || []).map(stripId),
        extra_lines: (src.extra_lines || []).map(stripId),
        created_at: undefined,
        updated_at: undefined,
      };
      delete payload.id;
      const res = await createQuote(payload);
      const created = res;
      toast.success("Offerte gedupliceerd");
      setQuotes((qs) => [created, ...qs]);
      setActiveId(created.id);
      setQuote({ ...emptyQuote, ...created });
    } catch (e) {
      console.error("duplicateQuote failed", e);
      toast.error("Dupliceren mislukt");
    }
  };
  const switchQuote = (id) => {
    const q = quotes.find((x) => x.id === id);
    if (q) { setActiveId(id); setQuote({ ...emptyQuote, ...q }); }
  };

  const save = useCallback(async (options = {}) => {
    const { silent = false } = options;
    setSaving(true);
    setSyncStatus("saving");
    // v6 F5 — snapshot edit counter VÓÓR de PUT-call. Als er tijdens de
    // roundtrip nieuwe edits gebeurden, mogen we setDirty(false) niet doen.
    const startRev = editRevRef.current;
    try {
      const payload = { ...quote, project_id: projectId };
      let saved;
      const wasCreate = !activeId;
      if (activeId) saved = await updateQuote(activeId, payload);
      else saved = await createQuote(payload);
      // Local merge instead of refetching all quotes — avoids selector flicker.
      setQuotes((prev) => {
        const exists = prev.some((q) => q.id === saved.id);
        const next = exists ? prev.map((q) => q.id === saved.id ? saved : q) : [saved, ...prev];
        // v6 H — keep sessionStorage cache fresh after each save
        cacheWrite({ quotes: next, activeId: saved.id });
        return next;
      });
      if (wasCreate) {
        // New quote: assign id locally + sync any backend-generated fields
        // (quote_number, created_at) without disturbing user's current edits.
        setActiveId(saved.id);
        setQuote((q) => ({
          ...q,
          id: saved.id,
          quote_number: saved.quote_number ?? q.quote_number,
          created_at: saved.created_at ?? q.created_at,
        }));
      }
      // v6 F5 — alleen "synced" markeren als er géén edits gebeurden tijdens
      // de PUT-roundtrip. Anders: dirty blijft true → autosave triggert
      // opnieuw na 1500ms en saved de meest recente state.
      if (editRevRef.current === startRev) {
        setDirty(false);
        setSyncStatus("synced");
      } else {
        // Edits tijdens save — laat dirty=true zodat autosave opnieuw triggert.
        setSyncStatus("local");
      }
      if (!silent) toast.success("Offerte opgeslagen");
      return saved;
    } catch {
      setSyncStatus("local");
      if (!silent) toast.error("Fout bij opslaan");
    }
    finally { setSaving(false); }
  }, [quote, activeId, projectId]);

  // v6 H — Silent debounced auto-save. Triggers 1500ms after the last edit
  // when the quote is dirty and online. Keeps the manual "Opslaan" button as
  // a safety net but most saves now happen invisibly in the background.
  // Uses the latest `save` closure via ref to avoid stale-quote captures.
  const saveRef = useRef(save);
  useEffect(() => { saveRef.current = save; }, [save]);
  useEffect(() => {
    if (!dirty || !isOnline || saving) return;
    const t = setTimeout(() => {
      saveRef.current?.({ silent: true });
    }, 1500);
    return () => clearTimeout(t);
  }, [dirty, isOnline, saving, quote]);

  // v6 F42k Correctieronde v10 P0-FIX 2 — Flush pending autosave op unmount.
  // Zonder deze cleanup verloor de user zijn laatste edits wanneer hij binnen
  // de 1500ms debounce-window wegnavigeerde (naar Projecten/Klanten/Prijslijst
  // of de offerte sloot). Bij terugkeer werd de gecachte oude state uit
  // sessionStorage geladen → recente selecties/overrides waren verdwenen.
  // Ref-based dirty-check zodat de cleanup altijd de LAATSTE state ziet.
  const dirtyRef = useRef(dirty);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  useEffect(() => {
    // Flush pending edits bij tab-hide, browser-refresh en unmount.
    const flush = () => {
      if (dirtyRef.current && saveRef.current) {
        try { saveRef.current({ silent: true }); } catch { /* ignore */ }
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      flush();
    };
  }, []);

  const downloadPdf = async () => {
    const saved = await save();
    const id = activeId || saved?.id;
    if (id) {
      // Show result modal instead of just opening PDF
      const fresh = quotes.find((x) => x.id === id) || saved;
      setPdfResult({
        url: quotePdfUrl(id),
        id,
        quote_number: fresh?.quote_number || saved?.quote_number || id.slice(0, 8).toUpperCase(),
        date: new Date().toLocaleDateString("nl-BE"),
        title: fresh?.title || quote.title,
      });
    }
  };

  const sharePdf = async (url) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Offerte ${pdfResult?.quote_number}`,
          text: `Offerte ${pdfResult?.quote_number}`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("PDF-link gekopieerd");
      }
    } catch { /* user cancelled */ }
  };

  // v14at — E-mail-knop in "Offerte gegenereerd"-modal opent nu een
  // compose-dialog die de bestaande SMTP-flow gebruikt (met verse PDF
  // als bijlage via /api/quotes/{id}/send-email). Wanneer geen mailbox
  // gekoppeld is toont de dialog een fallback naar mailto:.
  const [emailComposeOpen, setEmailComposeOpen] = useState(false);
  const emailPdf = (_url) => {
    setEmailComposeOpen(true);
  };

  const removeCurrent = async () => {
    if (!activeId) return;
    await deleteQuote(activeId);
    toast.success("Verwijderd");
    const qs = await listQuotes(projectId);
    setQuotes(qs);
    if (qs[0]) { setActiveId(qs[0].id); setQuote({ ...emptyQuote, ...qs[0] }); }
    else newQuote();
  };

  const applyTemplate = async (tpl) => {
    // Remap IDs so deduplication works when applied multiple times
    const idMap = {};
    const mappedRooms = (tpl.rooms || []).map((r) => {
      const nid = uuid(); idMap[r.id] = nid;
      return { ...r, id: nid };
    });
    const mappedMs = (tpl.measurements || []).map((m) => ({
      ...m, id: uuid(),
      room_id: m.room_id ? (idMap[m.room_id] || null) : null,
      deductions: (m.deductions || []).map((d) => ({ ...d, id: uuid() })),
    }));
    const mappedExtras = (tpl.extra_lines || []).map((e) => ({
      ...e, id: uuid(),
      room_id: e.room_id ? (idMap[e.room_id] || null) : null,
    }));
    // v6 E7 — also import v6 template_lines (used by exterior/woodwork/wallpaper
    // templates that don't bake rooms+measurements). For an exterior project
    // these lines target the first existing exterior zone (or create one);
    // for interior projects they're added unlinked so user can route them later.
    const tplLines = tpl.template_lines || [];
    const pcById = Object.fromEntries(priceCodes.map((p) => [p.id, p]));
    const isExteriorTpl = ["exterior", "woodwork"].includes((tpl.category || "").toLowerCase());
    const extraMs = [];
    let extraZone = null;
    if (tplLines.length > 0) {
      // Find or create target zone for exterior-style templates
      let targetZoneId = null;
      if (isExteriorTpl) {
        const existingZone = (quote.work_zones || []).find((z) =>
          ["exterior_area", "woodwork", "general"].includes(z.zone_type)
        );
        if (existingZone) {
          targetZoneId = existingZone.id;
        } else {
          extraZone = {
            id: uuid(),
            name: tpl.name || "Werkzone",
            zone_type: isExteriorTpl && (tpl.category === "woodwork") ? "woodwork" : "exterior_area",
            notes: "",
            order: (quote.work_zones || []).length,
            calc_mode: "manual",
            area: 0, dim_length: 0, dim_height: 0, dim_height2: 0,
            deductions: [], photos: [],
          };
          targetZoneId = extraZone.id;
        }
      }
      // Convert each template_line → measurement
      for (const ln of tplLines) {
        if (ln.default_checked === false) continue;
        const pc = pcById[ln.price_code_id];
        if (!pc) continue;
        const unit = pc.unit || "m2";
        // For m² codes targeting a work zone, use formula=zone_net so qty inherits net area.
        // For interior (no zone) keep formula="custom" with manual qty.
        let formula;
        if (unit === "m2" && targetZoneId) formula = "zone_net";
        else if (unit === "m2") formula = "custom";
        else if (unit === "lm") formula = "linear";
        else if (unit === "piece") formula = "piece";
        else if (unit === "hour") formula = "hour";
        else formula = "fixed";
        extraMs.push({
          id: uuid(),
          zone_id: targetZoneId,
          room_id: null,
          description: ln.description_override || pc.description,
          formula,
          length: 0, width: 0, height: 0,
          quantity: ln.suggested_qty != null ? +ln.suggested_qty : 0,
          unit_price_override: ln.unit_price_override != null ? +ln.unit_price_override : null,
          multiplier: 1, coats: 2,
          price_code_id: pc.id,
          action_code: pc.code,
          deductions: [],
        });
      }
    }

    setQuoteDirty((q) => ({
      ...q,
      description: (() => {
        // v6 F4 — hiërarchie: project-/offerte-omschrijving (override) >
        // sjabloon-standaardomschrijving. Vul automatisch in als de offerte
        // nog geen eigen omschrijving heeft. Bij conflict: vraag bevestiging.
        const tplDesc = (tpl.default_quote_description || tpl.description || "").trim();
        const cur = (q.description || "").trim();
        if (!tplDesc) return q.description;
        if (!cur) return tplDesc;
        if (cur === tplDesc) return q.description;
        const ok = typeof window !== "undefined" && window.confirm
          ? window.confirm(
              `De huidige offerteomschrijving vervangen door de standaardtekst van sjabloon "${tpl.name}"?\n\n` +
              `Klik OK om te vervangen, of Annuleren om de huidige tekst te behouden.`
            )
          : false;
        return ok ? tplDesc : q.description;
      })(),
      rooms: [...q.rooms, ...mappedRooms],
      work_zones: extraZone ? [...(q.work_zones || []), extraZone] : (q.work_zones || []),
      measurements: [...q.measurements, ...mappedMs, ...extraMs],
      extra_lines: [...q.extra_lines, ...mappedExtras],
    }));
    // v6 E1 — sticky context: persist active template on the project so new
    // rooms/zones inherit the template's defaults instead of standard painting.
    const cat = (tpl.category || "").toLowerCase();
    const slot = ["interior", "lacquer", "decorative", "wallpaper"].includes(cat) ? "interior_id"
              : ["exterior", "woodwork"].includes(cat) ? "exterior_id"
              : null;
    if (slot && project?.id) {
      try {
        const updated = await setProjectActiveTemplate(project.id, { [slot]: tpl.id, partial: true });
        setProject(updated);
      } catch (e) { /* non-blocking */ }
    }
    const summary = extraMs.length > 0
      ? `Sjabloon "${tpl.name}" toegepast — ${extraMs.length} werklijnen`
      : `Sjabloon "${tpl.name}" toegepast`;
    toast.success(summary);
  };

  const saveAsTemplate = async () => {
    if (!tplForm.name.trim()) { toast.error("Naam is verplicht"); return; }
    try {
      await createTemplate({
        name: tplForm.name,
        description: tplForm.description,
        rooms: quote.rooms,
        measurements: quote.measurements,
        extra_lines: quote.extra_lines,
      });
      toast.success("Sjabloon opgeslagen");
      setSaveTplOpen(false);
      setTplForm({ name: "", description: "" });
      setTemplates(await listTemplates({ active_only: false }));
    } catch { toast.error("Fout bij opslaan"); }
  };

  const applyQuickCalc = () => {
    if (!quickCalcRoom) return;
    const L = +quickCalcForm.length || 0;
    const W = +quickCalcForm.width || 0;
    const H = +quickCalcForm.height || 0;
    const wallPc = codeByCode["S-02.01"];
    const ceilingPc = codeByCode["S-02.02"];
    const adds = [];
    if (quickCalcForm.walls) {
      // Generate two wall lines (long pair + short pair) for editability
      adds.push(newMeasurement({
        room_id: quickCalcRoom.id, formula: "wall",
        description: "Lange wanden (L × H)",
        length: L, height: H, multiplier: 2,
        price_code_id: wallPc?.id || null,
      }));
      adds.push(newMeasurement({
        room_id: quickCalcRoom.id, formula: "wall",
        description: "Korte wanden (B × H)",
        length: W, height: H, multiplier: 2,
        price_code_id: wallPc?.id || null,
      }));
    }
    if (quickCalcForm.ceiling) {
      adds.push(newMeasurement({
        room_id: quickCalcRoom.id, formula: "ceiling",
        description: "Plafond (L × B)",
        length: L, width: W,
        price_code_id: ceilingPc?.id || null,
      }));
    }
    setQuoteDirty((q) => ({ ...q, measurements: [...q.measurements, ...adds] }));
    setQuickCalcRoom(null);
    toast.success(`${adds.length} regels toegevoegd aan ${quickCalcRoom.name}`);
  };

  const toggleRoom = (id) => {
    setCollapsed((s) => {
      const next = { ...s, [id]: !s[id] };
      try {
        if (quote?.id) {
          localStorage.setItem(`emma:roomCollapsed:${quote.id}`, JSON.stringify(next));
        }
      } catch (e) { /* localStorage may be unavailable / quota-full — silent */ }
      return next;
    });
  };

  // v6 F2 — Load persisted collapse map when the active quote changes.
  // v6 F10 — Default behavior on first open: ALLE rooms collapsed (vroeger
  // bleef de eerste open). Beta-feedback: gebruiker wil eerst de volledige
  // kamerlijst zien en zelf één kamer openen.
  useEffect(() => {
    if (!quote?.id) return;
    try {
      const raw = localStorage.getItem(`emma:roomCollapsed:${quote.id}`);
      if (raw) {
        setCollapsed(JSON.parse(raw) || {});
        return;
      }
    } catch (e) { /* ignore */ }
    // Default: collapse alle rooms (incl. de eerste).
    const ids = (quote.rooms || []).map((r) => r.id);
    const init = {};
    ids.forEach((id) => { init[id] = true; });
    setCollapsed(init);
  }, [quote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // v6 F12d — Zones + offerte-details collapse persistentie per quote.
  useEffect(() => {
    if (!quote?.id) return;
    try {
      const rawZ = localStorage.getItem(`emma:zoneCollapsed:${quote.id}`);
      if (rawZ) setZoneCollapsed(JSON.parse(rawZ) || {});
      else setZoneCollapsed({});
      const rawOD = localStorage.getItem(`emma:offerteDetailsOpen:${quote.id}`);
      if (rawOD !== null) setOfferteDetailsOpen(rawOD === "1");
      else setOfferteDetailsOpen(true);
    } catch (e) { /* ignore */ }
  }, [quote?.id]);

  const toggleZoneCollapsed = (zid, val) => {
    setZoneCollapsed((s) => {
      const next = { ...s, [zid]: !!val };
      try {
        if (quote?.id) localStorage.setItem(`emma:zoneCollapsed:${quote.id}`, JSON.stringify(next));
      } catch { /* ignore */ }
      return next;
    });
  };

  const setAllCollapsed = (val) => {
    // v6 F12d — Bulk "Alles open / dicht" voor kamers + werkzones samen.
    const nextRooms = Object.fromEntries((quote.rooms || []).map((r) => [r.id, val]));
    const nextZones = Object.fromEntries((quote.work_zones || []).map((z) => [z.id, val]));
    setCollapsed(nextRooms);
    setZoneCollapsed(nextZones);
    try {
      if (quote?.id) {
        localStorage.setItem(`emma:roomCollapsed:${quote.id}`, JSON.stringify(nextRooms));
        localStorage.setItem(`emma:zoneCollapsed:${quote.id}`, JSON.stringify(nextZones));
      }
    } catch { /* ignore */ }
  };

  const toggleOfferteDetails = () => {
    setOfferteDetailsOpen((v) => {
      const next = !v;
      try {
        if (quote?.id) localStorage.setItem(`emma:offerteDetailsOpen:${quote.id}`, next ? "1" : "0");
      } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <AutoSourcesContext.Provider value={autoSources}>
    <div className="space-y-6" data-testid="paintmeasure-page">
      {/* Top bar */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/projecten" className="text-sm text-[#4F4F4D] hover:text-[#D96C4A] flex items-center gap-1" data-testid="back-projects">
            <ArrowLeft className="w-3.5 h-3.5" /> Terug naar projecten
          </Link>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#5D7259] mt-3">PaintMeasure™</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2C2C2A] leading-tight">{project?.name || "Project"}</h1>
          <p className="text-[#4F4F4D] text-sm mt-1">
            {client?.name || "—"}{project?.location ? ` · ${project.location}` : ""}
            {project?.default_vat_rate != null && (
              <span className="ml-2 text-xs px-2 py-0.5 rounded bg-[#F0EFEA]">BTW project: {project.default_vat_rate}%</span>
            )}
          </p>
          <SyncStatusBadge status={syncStatus} online={isOnline} dirty={dirty} />
          <button
            type="button"
            onClick={showAutoSaveTipManually}
            className="ml-2 inline-flex items-center justify-center w-6 h-6 rounded-full text-[#5D7259] hover:text-[#D96C4A] hover:bg-[#FBE9DF] align-middle"
            title="Info auto-opslag"
            data-testid="autosave-tip-reopen"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
          {/* v14am — Project-brede AutoFill-knop is verwijderd. AutoFill leeft nu
              per-gevelvlak in FacadeSurfaceRow (context-safe target). */}
          {/* v6 E1 — sticky template context badges (interior + exterior).
              v6 F11.7 — Toont expliciet "Template niet gevonden" wanneer het
              opgeslagen template_id niet meer bestaat in de bibliotheek. */}
          {(activeInteriorTemplate || activeExteriorTemplate || interiorTemplateMissing || exteriorTemplateMissing) && (
            <div className="mt-2 flex flex-wrap gap-2" data-testid="active-template-badges">
              {activeInteriorTemplate && (
                <span className="inline-flex items-center gap-1.5 text-xs px-2.5 h-7 rounded-full bg-[#F2F5EE] border border-[#6B8E6B]/40 text-[#3F5E3F]" data-testid="active-template-interior">
                  <Sparkles className="w-3 h-3" />
                  Binnen: <strong className="font-semibold">{activeInteriorTemplate.name}</strong>
                  <button
                    onClick={async () => {
                      await setProjectActiveTemplate(project.id, { interior_id: null, exterior_id: project?.active_template_exterior_id ?? null, partial: false });
                      setProject((p) => ({ ...p, active_template_interior_id: null }));
                      toast.success("Sjabloon-context binnen gewist");
                    }}
                    className="ml-1 text-[#6B8E6B] hover:text-[#D9534F] font-bold"
                    title="Wis sjabloon-context"
                    data-testid="clear-template-interior"
                  >×</button>
                </span>
              )}
              {interiorTemplateMissing && (
                <span
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 h-7 rounded-full bg-[#FCE9E6] border border-[#D9534F]/50 text-[#A03127]"
                  data-testid="missing-template-interior"
                  title={`Template-ID ${project?.active_template_interior_id} bestaat niet meer in de bibliotheek.`}
                >
                  ⚠ Binnen: Template niet gevonden
                  <button
                    onClick={async () => {
                      await setProjectActiveTemplate(project.id, { interior_id: null, exterior_id: project?.active_template_exterior_id ?? null, partial: false });
                      setProject((p) => ({ ...p, active_template_interior_id: null }));
                      toast.success("Verwijzing naar verdwenen template gewist");
                    }}
                    className="ml-1 text-[#A03127] hover:text-[#D9534F] font-bold"
                    title="Verwijs-link wissen"
                    data-testid="clear-missing-template-interior"
                  >×</button>
                </span>
              )}
              {activeExteriorTemplate && (
                <span className="inline-flex items-center gap-1.5 text-xs px-2.5 h-7 rounded-full bg-[#FFF1E6] border border-[#D96C4A]/40 text-[#A04A2D]" data-testid="active-template-exterior">
                  <Sparkles className="w-3 h-3" />
                  Buiten: <strong className="font-semibold">{activeExteriorTemplate.name}</strong>
                  <button
                    onClick={async () => {
                      await setProjectActiveTemplate(project.id, { interior_id: project?.active_template_interior_id ?? null, exterior_id: null, partial: false });
                      setProject((p) => ({ ...p, active_template_exterior_id: null }));
                      toast.success("Sjabloon-context buiten gewist");
                    }}
                    className="ml-1 text-[#D96C4A] hover:text-[#D9534F] font-bold"
                    title="Wis sjabloon-context"
                    data-testid="clear-template-exterior"
                  >×</button>
                </span>
              )}
              {exteriorTemplateMissing && (
                <span
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 h-7 rounded-full bg-[#FCE9E6] border border-[#D9534F]/50 text-[#A03127]"
                  data-testid="missing-template-exterior"
                  title={`Template-ID ${project?.active_template_exterior_id} bestaat niet meer in de bibliotheek.`}
                >
                  ⚠ Buiten: Template niet gevonden
                  <button
                    onClick={async () => {
                      await setProjectActiveTemplate(project.id, { interior_id: project?.active_template_interior_id ?? null, exterior_id: null, partial: false });
                      setProject((p) => ({ ...p, active_template_exterior_id: null }));
                      toast.success("Verwijzing naar verdwenen template gewist");
                    }}
                    className="ml-1 text-[#A03127] hover:text-[#D9534F] font-bold"
                    title="Verwijs-link wissen"
                    data-testid="clear-missing-template-exterior"
                  >×</button>
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {quotes.length > 0 && (
            <Select value={activeId || ""} onValueChange={switchQuote}>
              <SelectTrigger className="h-12 rounded-xl w-48" data-testid="quote-switcher"><SelectValue placeholder="Offerte" /></SelectTrigger>
              <SelectContent>
                {quotes.map((q) => (
                  <SelectItem key={q.id} value={q.id}>{q.title} · {new Date(q.created_at).toLocaleDateString("nl-BE")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* v6 F12e — "Sjabloon laden" verwijderd uit actiebalk (verwart met Emma™ werksjablonen).
              Sjablonen worden nu uitsluitend toegepast binnen kamers, werkzones en extra posten. */}
          <Button variant="outline" onClick={newQuote} className="h-12 rounded-xl gap-2" data-testid="new-quote-btn">
            <FilePlus2 className="w-4 h-4" /> Nieuwe
          </Button>
          {/* v6 F12e — "Als sjabloon" vervangen door "Offerte dupliceren" — kopie van bestaande offerte. */}
          <Button variant="outline" onClick={duplicateQuote} disabled={!activeId || saving} className="h-12 rounded-xl gap-2" data-testid="duplicate-quote-btn">
            <Copy className="w-4 h-4" /> Dupliceren
          </Button>
          <Button variant="outline" onClick={() => setShowSettings(s => !s)} className="h-12 rounded-xl gap-2" data-testid="quote-settings-btn">
            <SettingsIcon className="w-4 h-4" /> Instellingen
          </Button>
          <Button onClick={save} disabled={saving} className="h-12 rounded-xl bg-[#2C2C2A] hover:bg-black gap-2" data-testid="save-quote-btn">
            <Save className="w-4 h-4" /> Opslaan
          </Button>
          <Button onClick={downloadPdf} className="h-12 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] gap-2" data-testid="pdf-quote-btn">
            <FileDown className="w-4 h-4" /> PDF
          </Button>
          <Button variant="outline" disabled className="h-12 rounded-xl gap-2 opacity-60" data-testid="excel-export-btn"
            title="Excel export — binnenkort">
            <FileSpreadsheet className="w-4 h-4" /> Excel<Badge className="bg-[#D4A373] text-white text-[10px] ml-1">soon</Badge>
          </Button>
          <Button variant="outline" disabled className="h-12 rounded-xl gap-2 opacity-60" data-testid="peppol-export-btn"
            title="Peppol — binnenkort">
            <Send className="w-4 h-4" /> Peppol<Badge className="bg-[#8A9A86] text-white text-[10px] ml-1">soon</Badge>
          </Button>
          {activeId && (
            <Button variant="outline" onClick={removeCurrent} className="h-12 rounded-xl text-[#D9534F] border-[#D9534F] hover:bg-red-50" data-testid="delete-quote-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* v6 F1 — Subtle onboarding tip about auto-save (dismissible) */}
      {autoSaveTipVisible && (
        <div
          className="flex items-start gap-3 p-3 sm:p-4 rounded-xl border border-[#6B8E6B]/30 bg-[#F2F5EE] text-[#3F5E3F]"
          data-testid="autosave-tip"
          role="note"
        >
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm leading-snug">
            Je werk wordt automatisch bewaard. De knop <strong>Opslaan</strong> blijft beschikbaar als extra zekerheid.
          </div>
          <button
            type="button"
            onClick={dismissAutoSaveTip}
            className="flex-shrink-0 text-[#6B8E6B] hover:text-[#D9534F] p-1 -m-1"
            aria-label="Sluit melding"
            data-testid="autosave-tip-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: editor */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quote details — v6 F12d inklapbaar */}
          <div className="emma-card space-y-4" data-testid="quote-info-card">
            <button
              type="button"
              onClick={toggleOfferteDetails}
              className="w-full flex items-center gap-2 text-left"
              data-testid="offerte-details-toggle"
            >
              <ChevronDown className={`w-4 h-4 text-[#D96C4A] transition-transform ${offerteDetailsOpen ? "" : "-rotate-90"}`} />
              <Ruler className="w-5 h-5 text-[#D96C4A]" />
              <h2 className="font-heading text-xl font-semibold flex-1 min-w-0">
                Offerte details
              </h2>
              {!offerteDetailsOpen && (
                <>
                  {(quote.title || project?.name) && (
                    <span className="text-xs text-[#4F4F4D] truncate max-w-[240px] italic hidden sm:inline">
                      · {quote.title || project?.name}
                    </span>
                  )}
                  <span className="text-[10px] uppercase tracking-wider text-[#A8A7A0] hidden md:inline whitespace-nowrap">
                    Eenheidsprijzen {quote.show_unit_prices ? "aan" : "uit"} · BTW per regel {quote.show_vat_per_line ? "aan" : "uit"}
                  </span>
                </>
              )}
            </button>
            {offerteDetailsOpen && <>
            {/* v14g — AI Offerte Assistent panel */}
            <AIAssistantPanel
              quoteId={quote?.id}
              currentProjectName={project?.name || ""}
              onAcceptField={acceptAIField}
              onUpdateProjectName={updateProjectNameFromAI}
              changeFingerprint={aiChangeFingerprint}
              currentFields={{
                title: quote?.title || "",
                description: quote?.description || "",
                works_included: quote?.works_included || "",
                remarks: quote?.remarks || "",
              }}
            />
            {/* v14j — Begeleidende e-mail voor deze offerte */}
            <CoverEmailPanel
              quoteId={quote?.id}
              quoteNumber={quote?.quote_number || ""}
              projectName={project?.name || ""}
              clientName={client?.name || ""}
              clientEmail={client?.email || ""}
              clientId={client?.id || ""}
              onQuoteStatusChange={(qs) => setProject((p) => (p ? { ...p, quote_status: qs } : p))}
            />
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-medium">Offertetitel</Label>
                {undoCount("title") > 0 && (
                  <button
                    type="button"
                    onClick={() => popUndoHistory("title")}
                    className="text-[11px] text-[#B07A4F] hover:underline inline-flex items-center gap-1"
                    data-testid="quote-title-undo-btn"
                    title="Herstel de vorige waarde (max 5 versies bewaard)"
                  >
                    <span aria-hidden>↶</span> Ongedaan maken ({undoCount("title")})
                  </button>
                )}
              </div>
              <Input value={quote.title} onChange={(e) => setField("title", e.target.value)}
                onFocus={() => captureUndoFocus("title")}
                onBlur={(e) => pushUndoHistory("title", e.target.value)}
                placeholder="Bv. Schilderwerken woonkamer + hal"
                className="h-12 rounded-xl" data-testid="quote-title-input" />
              <p className="text-xs text-[#6E6E6C]">
                Commerciële titel die op de offerte/PDF verschijnt. Laat leeg om automatisch de Projectnaam te gebruiken.
              </p>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-medium">Omschrijving offerte</Label>
                {undoCount("description") > 0 && (
                  <button
                    type="button"
                    onClick={() => popUndoHistory("description")}
                    className="text-[11px] text-[#B07A4F] hover:underline inline-flex items-center gap-1"
                    data-testid="quote-description-undo-btn"
                    title="Herstel de vorige waarde (max 5 versies bewaard)"
                  >
                    <span aria-hidden>↶</span> Ongedaan maken ({undoCount("description")})
                  </button>
                )}
              </div>
              <Textarea value={quote.description} onChange={(e) => setField("description", e.target.value)}
                onFocus={() => captureUndoFocus("description")}
                onBlur={(e) => pushUndoHistory("description", e.target.value)}
                rows={3} placeholder="Korte introductietekst die direct onder de titel verschijnt"
                className="rounded-xl" data-testid="quote-description-input" />
              <p className="text-xs text-[#6E6E6C]">Regeleindes en bullets blijven behouden op de PDF.</p>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-medium">De werken omvatten</Label>
                <div className="flex items-center gap-3">
                  {undoCount("works_included") > 0 && (
                    <button
                      type="button"
                      onClick={() => popUndoHistory("works_included")}
                      className="text-[11px] text-[#B07A4F] hover:underline inline-flex items-center gap-1"
                      data-testid="quote-works-undo-btn"
                      title="Herstel de vorige waarde (max 5 versies bewaard)"
                    >
                      <span aria-hidden>↶</span> Ongedaan maken ({undoCount("works_included")})
                    </button>
                  )}
                  {quote.works_included_locked && (
                    <button
                      type="button"
                      onClick={() => setQuote((q) => ({ ...q, works_included_locked: false, works_included: "" }))}
                      className="text-[11px] text-[#B07A4F] hover:underline"
                      data-testid="quote-works-unlock-btn"
                      title="Wissel weer naar automatische sjabloontekst bij template-keuze"
                    >
                      Reset naar sjabloontekst
                    </button>
                  )}
                </div>
              </div>
              <Textarea
                value={quote.works_included}
                onFocus={() => captureUndoFocus("works_included")}
                onBlur={(e) => pushUndoHistory("works_included", e.target.value)}
                onChange={(e) => {
                  // v6 F29 — Zodra user manueel typt, lock de tekst zodat
                  // een daaropvolgende sjabloonwissel de aangepaste tekst
                  // niet meer overschrijft.
                  setQuote((q) => ({ ...q, works_included: e.target.value, works_included_locked: true }));
                }}
                rows={4}
                placeholder="Wordt automatisch gevuld op basis van de gekozen sjabloon(en). Manueel aanpassen mag."
                className="rounded-xl"
                data-testid="quote-works-input"
              />
              <p className="text-xs text-[#6E6E6C]">Regeleindes en bullets (• of -) blijven behouden op de PDF.</p>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-medium">Opmerkingen</Label>
                {undoCount("remarks") > 0 && (
                  <button
                    type="button"
                    onClick={() => popUndoHistory("remarks")}
                    className="text-[11px] text-[#B07A4F] hover:underline inline-flex items-center gap-1"
                    data-testid="quote-remarks-undo-btn"
                    title="Herstel de vorige waarde (max 5 versies bewaard)"
                  >
                    <span aria-hidden>↶</span> Ongedaan maken ({undoCount("remarks")})
                  </button>
                )}
              </div>
              <Textarea value={quote.remarks} onChange={(e) => setField("remarks", e.target.value)}
                onFocus={() => captureUndoFocus("remarks")}
                onBlur={(e) => pushUndoHistory("remarks", e.target.value)}
                rows={2} placeholder="Bv. voorwaarden, planning, varianten"
                className="rounded-xl" data-testid="quote-remarks-input" />
            </div>
            {/* v6 F35 — BTW-tarief project direct bewerkbaar vanuit Offerte details.
                Deelt project.default_vat_rate, dus wijziging updates het project
                (zelfde bron als projectmodal). Preview + PDF herberekenen. */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">BTW-tarief project</Label>
              <Select
                value={String(project?.default_vat_rate ?? 21)}
                onValueChange={async (v) => {
                  const rate = parseInt(v, 10);
                  // Optimistische UI-update zodat de preview meteen herrekent.
                  setProject((p) => ({ ...p, default_vat_rate: rate }));
                  try {
                    const payload = {
                      name: project?.name,
                      description: project?.description,
                      status: project?.status,
                      client_id: project?.client_id,
                      quote_status: project?.quote_status,
                      default_vat_rate: rate,
                      project_type: project?.project_type ?? "mixed",
                      template_id: null,
                    };
                    const updated = await updateProject(project.id, payload);
                    setProject(updated);
                  } catch (err) {
                    console.error("BTW-update mislukt:", err);
                    // Rollback wanneer save faalt.
                    setProject((p) => ({ ...p, default_vat_rate: project?.default_vat_rate ?? 21 }));
                  }
                }}
              >
                <SelectTrigger
                  className="h-12 rounded-xl"
                  data-testid="offerte-vat-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0, 6, 21].map((rate) => (
                    <SelectItem
                      key={rate}
                      value={String(rate)}
                      data-testid={`offerte-vat-option-${rate}`}
                    >
                      {rate}%
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#6E6E6C]">
                Standaardtarief voor deze offerte. De offerte-preview en PDF gebruiken
                dit tarief. Delen dezelfde waarde als het BTW-veld in de projectkaart.
              </p>
            </div>
            <div className="flex items-center justify-between border border-[#E5E2DC] rounded-xl px-4 h-12">
              <Label className="cursor-pointer text-sm">Eenheidsprijzen tonen op offerte</Label>
              <Switch checked={!!quote.show_unit_prices}
                onCheckedChange={(v) => setField("show_unit_prices", v)}
                data-testid="show-unit-prices-switch" />
            </div>
            {/* v6 F12c — Per-regel BTW-kolom in PDF-tabel. Default UIT.
                Aan → toont bv. 6% / 21% per regel (handig bij gemengde tarieven).
                Uit → BTW enkel onderaan uitgesplitst per gebruikt tarief. */}
            <div className="flex items-start justify-between border border-[#E5E2DC] rounded-xl px-4 py-3 gap-3">
              <div className="flex-1 min-w-0">
                <Label className="cursor-pointer text-sm block">BTW-percentage tonen per regel op offerte</Label>
                <p className="text-[11px] text-[#4F4F4D] mt-0.5 leading-tight">
                  Uit → PDF blijft rustig; BTW enkel onderaan per tarief. Aan → aparte "BTW"-kolom per regel (bij gemengde tarieven).
                </p>
              </div>
              <Switch checked={!!quote.show_vat_per_line}
                onCheckedChange={(v) => setField("show_vat_per_line", v)}
                data-testid="show-vat-per-line-switch" />
            </div>
            </>}
          </div>

          {/* Display settings — collapsible met chevron in kop. Default DICHT
              (showSettings=false); "Instellingen"-knop bovenaan opent het,
              klik op de header sluit het weer. */}
          {showSettings && (
            <div className="emma-card !p-0 overflow-hidden" data-testid="quote-settings-card">
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-[#FAF9F6] transition"
                aria-expanded={true}
                aria-controls="quote-settings-body"
                data-testid="quote-settings-toggle"
              >
                <ChevronDown className="w-4 h-4 text-[#5D7259] flex-shrink-0" />
                <Eye className="w-4 h-4 text-[#D96C4A] flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <h2 className="font-heading text-lg font-semibold text-[#2C2C2A]">Offerte-instellingen</h2>
                  <p className="text-xs text-[#4F4F4D]">Bepaal welke kolommen op de PDF zichtbaar zijn.</p>
                </div>
              </button>
              <div id="quote-settings-body" className="px-5 pb-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    ["show_codes", "Codes tonen"],
                    ["show_quantities", "Hoeveelheden tonen"],
                    ["show_unit_prices", "Eenheidsprijzen tonen"],
                    ["show_layers", "Lagen-info tonen"],
                    ["show_vat_details", "BTW-details tonen (onderaan)"],
                    ["show_vat_per_line", "BTW-percentage per regel tonen"],
                    ["show_room_breakdown", "Per-kamer overzicht"],
                    ["show_signature_block", "Voor akkoord-blok in PDF"],
                    ["show_photos", "Foto's tonen in PDF (werkzones)"],
                  ].map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between border border-[#E5E2DC] rounded-lg px-3 h-11">
                      <Label className="text-sm cursor-pointer">{label}</Label>
                      <Switch checked={!!quote[key]} onCheckedChange={(v) => setField(key, v)} data-testid={`setting-${key}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* v6 F13 — Binnenzones toevoegen is nu altijd zichtbaar, ongeacht projecttype.
              §5B: op mobiel inklapbaar (chevron links). Desktop (lg+) blijft altijd open.
              v6 F41 — Hernoemd van "Ruimte toevoegen" naar "Binnenzones toevoegen"
              met bijhorende subtitel voor duidelijker onderscheid met buitenzones en werkdomeinen. */}
          <div className="emma-card !p-4" data-testid="room-presets-card">
            <button
              type="button"
              onClick={() => setRoomAddOpen((v) => !v)}
              className="w-full flex items-center gap-2 text-left lg:cursor-default"
              data-testid="room-presets-toggle"
              aria-expanded={roomAddOpen ? "true" : "false"}
            >
              <ChevronDown className={`w-4 h-4 text-[#D96C4A] transition-transform lg:hidden ${roomAddOpen ? "" : "-rotate-90"}`} />
              <Home className="w-4 h-4 text-[#D96C4A]" />
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#5D7259] flex-1">Binnenzones toevoegen</div>
              {!roomAddOpen && (quote.rooms || []).length > 0 && (
                <span className="text-[10px] text-[#A8A7A0] font-normal lg:hidden">
                  ({(quote.rooms || []).length} {(quote.rooms || []).length === 1 ? "ruimte" : "ruimtes"})
                </span>
              )}
            </button>
            <div className={`${roomAddOpen ? "block" : "hidden"} lg:block mt-3`}>
              <p className="text-xs text-[#4F4F4D] mb-3">Voeg ruimtes toe voor binnenschilderwerken, behangwerken of schilderstechnieken.</p>
              <div className="flex flex-wrap gap-2">
                {ROOM_TYPES.map((rt) => (
                  <Button key={rt.key} variant="outline" onClick={() => addRoom(rt.key)}
                    className="h-11 rounded-xl text-xs gap-1 hover:bg-[#D96C4A] hover:text-white hover:border-[#D96C4A]"
                    data-testid={`room-type-${rt.key}`}>
                    <Plus className="w-3 h-3" /> {rt.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* v6 F41 — "Werkzone toevoegen" opgesplitst in twee afzonderlijke blokken:
              (1) Buitenzones (gevels/tuinmuur/bijgebouw) en (2) Werkdomeinen
              (schrijnwerk & trapwerken). Zelfde onderliggende addZone-flow;
              alleen visuele indeling + labels wijzigen. */}
          <ExteriorZoneAddButtons
            onAddZone={addZone}
            showGeneralPresets={true}
            collapsed={!zoneAddOpen}
            onToggleCollapsed={() => setZoneAddOpen((v) => !v)}
            zoneCount={(quote.work_zones || []).filter((z) => z.zone_type === "exterior_area").length}
            title="Buitenzones toevoegen"
            subtitle="Voeg gevels, tuinmuren of buitenvlakken toe als afzonderlijke buitenzone."
            presetFilter={(p) => p.zone_type === "exterior_area"}
            testidSuffix="buiten"
            countLabel="buitenzone"
            countLabelPlural="buitenzones"
          />

          <ExteriorZoneAddButtons
            onAddZone={addZone}
            showGeneralPresets={true}
            collapsed={!workdomainAddOpen}
            onToggleCollapsed={() => setWorkdomainAddOpen((v) => !v)}
            zoneCount={(quote.work_zones || []).filter((z) => z.zone_type !== "exterior_area").length}
            title="Werkdomeinen toevoegen"
            subtitle="Voeg schrijnwerk of trapwerken toe als afzonderlijk werkpakket."
            presetFilter={(p) => p.zone_type !== "exterior_area"}
            testidSuffix="werkdomeinen"
            countLabel="werkdomein"
            countLabelPlural="werkdomeinen"
          />

          {/* v6 F12d — Sectie-header voor alle inklapbare inhoud (werkzones + kamers).
              v6 F12d.1 — Toont compacte totaalsamenvatting ENKEL wanneer alle
              kaarten ingeklapt zijn (rustige overhead-preview op mobiel). */}
          {(() => {
            const zones = quote.work_zones || [];
            const rooms = quote.rooms || [];
            const totalItems = zones.length + rooms.length;
            const showSectionHeader = totalItems >= 1;
            if (!showSectionHeader) return null;
            const allCollapsed = totalItems > 0
              && zones.every((z) => zoneCollapsed[z.id] === true)
              && rooms.every((r) => collapsed[r.id] === true);
            const subtotalExcl = +(calc?.subtotal_excl || 0);
            const subtotalNl = new Intl.NumberFormat("nl-BE", {
              style: "currency", currency: "EUR",
              minimumFractionDigits: 2, maximumFractionDigits: 2,
            }).format(subtotalExcl).replace(/\u00A0/g, " ");
            return (
              <div
                className="flex items-center justify-between px-2 gap-3 flex-wrap"
                data-testid="werkzones-rooms-section-header"
              >
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#5D7259] flex items-center gap-2 flex-wrap min-w-0">
                  <Home className="w-3.5 h-3.5 flex-shrink-0" />
                  Werkzones en ruimtes
                  <span
                    className="text-[10px] normal-case tracking-normal text-[#A8A7A0] font-normal ml-1"
                    data-testid="werkzones-rooms-summary"
                  >
                    ({totalItems} {totalItems === 1 ? "kaart" : "kaarten"}
                    {allCollapsed && (
                      <>
                        {" · "}
                        <strong className="text-[#5D7259] font-semibold">{subtotalNl}</strong>
                        {" excl. BTW"}
                      </>
                    )})
                  </span>
                </div>
                {totalItems > 1 && (
                  <div className="flex items-center gap-1 text-xs flex-shrink-0 whitespace-nowrap">
                    <button
                      onClick={() => setAllCollapsed(false)}
                      className="text-[#D96C4A] hover:underline px-2 py-1"
                      data-testid="expand-all-cards"
                    >
                      Alles open
                    </button>
                    <span className="text-[#A8A7A0]">·</span>
                    <button
                      onClick={() => setAllCollapsed(true)}
                      className="text-[#4F4F4D] hover:underline px-2 py-1"
                      data-testid="collapse-all-cards"
                    >
                      Alles dicht
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* v6 F13 — WorkZone flow is nu altijd zichtbaar. Projecttype
              beperkt niet langer welke werkzones getoond worden. */}
          <ExteriorZoneFlow
              hideAddButtons={true}
              zones={quote.work_zones || []}
              measurements={quote.measurements}
              priceCodes={priceCodes}
              templates={templates}
              partsDefaults={partsDefaults}
              projectVat={project?.default_vat_rate ?? 21}
              calc={calc}
              quoteSaved={!!activeId}
              showGeneralPresets={true}
              onAddZone={addZone}
              onUpdateZone={updateZone}
              onRemoveZone={removeZone}
              onAddMeasurement={addZoneMeasurement}
              onUpdateMeasurement={updM}
              onRemoveMeasurement={removeM}
              onPhotoUpload={uploadPhoto}
              onPhotoRemove={removePhoto}
              onPhotoSetLabel={setPhotoLabel}
              zoneCollapsed={zoneCollapsed}
              onToggleZoneCollapsed={toggleZoneCollapsed}
              autoMeasureEnabled={!!company?.automeasure_enabled}
              renderAutoMeasureBlock={(zone) => (
                <AutoMeasureZoneBlock
                  zone={zone}
                  // v14at-p4 F4.3 (Defect E) — Expliciete project-context.
                  // Zone-subdocumenten in quote.work_zones hebben geen
                  // project_id back-reference; we gebruiken de canonieke
                  // waarde uit PaintMeasure (project.id uit de URL-geladen
                  // project-state; quote.project_id als consistency-fallback).
                  projectId={project?.id || quote?.project_id || null}
                  defaultAddress={canonicalizeAddress(project?.worksite_address || project?.location || client?.address || "")}
                  automeasureEnabled={!!company?.automeasure_enabled}
                  moduleSupported={true}
                  hasExistingSurfaces={zoneHasMeaningfulSurfaces(zone)}
                  onMeasured={async (building, strategy, selectedComponentIds = null) => applyMeasurementToZone(zone.id, building, strategy, selectedComponentIds)}
                  onApplyServerAuthored={async (applyResponse) => applyServerAuthoredSurfacesToZone(zone.id, applyResponse)}
                  onDetailsOpen={() => openAutoMeasureDetails(zone)}
                />
              )}
            />

          {/* v6 F13 — Rooms zijn nu altijd getoond, ongeacht projecttype. */}
          {quote.rooms.length === 0 && (project?.project_type === "interior") && (
            <div className="emma-card text-center py-12 text-[#4F4F4D]">
              Voeg een kamer toe om te starten met meten.
            </div>
          )}
          {quote.rooms.map((r) => {
            const rMeasurements = quote.measurements.filter((m) => m.room_id === r.id);
            const rExtras = quote.extra_lines.filter((e) => e.room_id === r.id);
            const rSummary = calc.rooms_summary.find((x) => x.id === r.id);
            const isAdvanced = !!advancedRooms[r.id];
            const inner = isAdvanced ? (
              <RoomCard key={r.id}
                room={r}
                measurements={rMeasurements}
                extras={rExtras}
                summary={rSummary}
                collapsed={collapsed[r.id]}
                onToggle={() => toggleRoom(r.id)}
                onRename={(name) => updateRoom(r.id, { name })}
                onRemove={() => removeRoom(r.id)}
                priceCodes={priceCodes}
                onAddMeasurement={(preset) => addMeasurement(r.id, preset)}
                onAddExtra={(preset) => addExtra(r.id, preset)}
                onQuickCalc={() => setQuickCalcRoom(r)}
                onUpdateM={updM} onRemoveM={removeM} onDuplicateM={duplicateM}
                onAddDeduction={addDeduction} onUpdDeduction={updDeduction} onRemDeduction={remDeduction}
                onUpdExtra={updExtra} onRemExtra={remExtra}
                onSwitchSimple={() => setAdvancedRooms((s) => ({ ...s, [r.id]: false }))}
              />
            ) : (
              <RoomQuickFlow key={r.id}
                room={r}
                measurements={rMeasurements}
                extras={rExtras}
                summary={rSummary}
                priceCodes={priceCodes}
                codeByCode={codeByCode}
                projectVat={project?.default_vat_rate ?? 21}
                activeTemplateActions={getRoomActions(r)}
                roomTemplateOptions={roomTemplateOptions}
                onChangeRoomTemplate={(tplId) => requestChangeRoomTemplate(r, tplId)}
                onUpdateRoom={(patch) => updateRoom(r.id, patch)}
                onRemove={() => removeRoom(r.id)}
                onToggleAction={(action) => toggleRoomAction(r, action)}
                onAddCustomMeasurement={(preset) => addMeasurement(r.id, preset)}
                onUpdateM={updM}
                onRemoveM={removeM}
                onSwitchAdvanced={() => setAdvancedRooms((s) => ({ ...s, [r.id]: true }))}
                collapsed={!!collapsed[r.id]}
                onToggleCollapsed={() => toggleRoom(r.id)}
                templates={templates}
              />
            );
            return <div key={r.id} data-zone-anchor={r.id}>{inner}</div>;
          })}

          {/* v6 F5 — Orphan measurements rescue: ongeldige/niet-toegewezen
              regels die niet aan een kamer of zone gekoppeld zijn. Ontstaan
              meestal via sjabloon-toepassing of bij het verwijderen van een
              kamer waar nog losse meetlijnen aan hingen. Zonder deze sectie
              waren ze enkel zichtbaar in de offerte-preview als "Algemeen"
              maar niet bewerkbaar — wat de bug "regel blijft meetellen" veroorzaakte.
              v6 F12 — inklapbaar gemaakt om de meetpagina rustiger te houden. */}
          {(() => {
            const orphans = quote.measurements.filter((m) => !m.room_id && !m.zone_id);
            if (orphans.length === 0) return null;
            return (
              <OrphanMeasurementsCard
                orphans={orphans}
                codeIndex={codeIndex}
                onRemove={removeM}
              />
            );
          })()}

          {/* v6 F11.3 — Projectbrede "Extra posten / opties" sectie
              (vervangt de oude "Algemene extra posten"-kaart). */}
          <ExtraPostenCard
            extras={quote.extra_lines.filter((e) => !e.room_id)}
            templates={templates}
            priceCodes={priceCodes}
            onAdd={(newLine) =>
              setQuoteDirty((q) => ({ ...q, extra_lines: [...q.extra_lines, newLine] }))
            }
            onUpdate={updExtra}
            onRemove={remExtra}
          />

          {/* Documenten */}
          {quotes.length > 0 && (
            <div className="emma-card" data-testid="documents-card">
              <div className="flex items-center gap-2 mb-3">
                <FileDown className="w-4 h-4 text-[#D96C4A]" />
                <h3 className="font-heading font-semibold">Documenten</h3>
                <span className="text-xs text-[#4F4F4D]">({quotes.length} offerte{quotes.length === 1 ? "" : "s"})</span>
              </div>
              <div className="space-y-2">
                {quotes.map((q) => (
                  <div key={q.id} className="flex items-center justify-between gap-2 px-3 py-2 border border-[#E5E2DC] rounded-xl bg-white" data-testid={`document-row-${q.id}`}>
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-semibold text-[#2C2C2A]">{q.quote_number || q.id.slice(0, 8).toUpperCase()}</div>
                      <div className="text-xs text-[#4F4F4D] truncate">
                        {q.title} · {new Date(q.created_at).toLocaleDateString("nl-BE")}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => switchQuote(q.id)} className="h-9 px-3 rounded-lg text-xs font-medium border border-[#E5E2DC] hover:bg-[#F0EFEA]" data-testid={`open-doc-${q.id}`}>
                        Open
                      </button>
                      <button
                        onClick={async () => {
                          try { await openQuotePdf(q.id); }
                          catch (e) {
                            const s = e?.response?.status;
                            if (s === 401) toast.error("Sessie verlopen. Log opnieuw in.");
                            else if (s === 404) toast.error("Offerte niet gevonden.");
                            else toast.error("PDF kon niet worden geopend.");
                          }
                        }}
                        className="h-9 px-3 rounded-lg text-xs font-medium bg-[#D96C4A] text-white hover:bg-[#C05A3C] flex items-center gap-1"
                        data-testid={`pdf-doc-${q.id}`}
                      >
                        <FileDown className="w-3 h-3" /> PDF
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Live preview */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 space-y-4" data-testid="live-summary">
            <div className="bg-[#2C2C2A] text-white rounded-2xl p-6 shadow-xl">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D4A373] flex items-center gap-1.5">
                <Calculator className="w-3.5 h-3.5" /> Offerte preview
              </div>
              <div className="font-heading text-4xl font-bold mt-2" data-testid="summary-total-incl">
                {fmtEUR(calc.total_incl)}
              </div>
              <div className="text-xs text-white/60 mt-1">
                incl. BTW{calc.total_vat > 0 && Object.entries(calc.vat_breakdown).filter(([, v]) => v > 0).length === 1 ? ` (${Object.entries(calc.vat_breakdown).find(([, v]) => v > 0)[0]}%)` : ""}
              </div>

              <div className="h-px bg-white/10 my-5" />

              <div className="space-y-1.5 text-sm">
                <SumRow label="Subtotaal (excl. BTW)" value={fmtEUR(calc.subtotal_excl)} testId="summary-subtotal" />
                {Object.entries(calc.vat_breakdown).map(([rate, amt]) =>
                  amt > 0 ? <SumRow key={rate} label={`BTW (${rate}%)`} value={fmtEUR(amt)} testId={`summary-vat-${rate}`} /> : null
                )}
              </div>

              {calc.rooms_summary.length > 0 && (
                <>
                  <div className="h-px bg-white/10 my-5" />
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-3">Per onderdeel</div>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {calc.rooms_summary.map((r) => (
                      <div key={r.id || "unassigned"} className="text-xs border border-white/10 rounded-lg p-2.5" data-testid={`room-summary-${r.id || 'unassigned'}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-[#D4A373]">{r.name}</span>
                          <span className="font-semibold">{fmtEUR(r.subtotal_excl)}</span>
                        </div>
                        {/* v6 E4 — only show wand-info when it's based on real room dims AND deductions apply */}
                        {r.walls_from_room_dims && r.walls_gross > 0 && r.walls_deduction > 0 && (
                          <div className="text-white/60 mt-1">
                            Wand: {r.walls_gross.toFixed(2)} m² bruto − {r.walls_deduction.toFixed(2)} aftrek = {r.walls_net.toFixed(2)} m² netto
                          </div>
                        )}
                        <div className="text-white/40 mt-0.5">{r.lines?.length || 0} regel(s)</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save template dialog */}
      <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
        <DialogContent className="max-w-md" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle>Bewaar als sjabloon</DialogTitle>
            <DialogDescription>Herbruik deze kamers, metingen en extra's in volgende projecten.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Sjabloonnaam *</Label>
              <Input value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })}
                placeholder="bv. Standaard appartement 2slpk" className="h-12 mt-1.5 rounded-xl" data-testid="template-name-input" />
            </div>
            <div>
              <Label>Omschrijving</Label>
              <Textarea value={tplForm.description} onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })}
                rows={2} className="mt-1.5 rounded-xl" data-testid="template-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTplOpen(false)} className="h-12 rounded-xl">Annuleer</Button>
            <Button onClick={saveAsTemplate} className="h-12 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C]" data-testid="template-save-btn">
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Room delete confirmation */}
      <AlertDialog open={!!confirmRoomDel} onOpenChange={(v) => !v && setConfirmRoomDel(null)}>
        <AlertDialogContent data-testid="room-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Kamer verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmRoomDel?.name}" en alle metingen + extra's binnen deze kamer worden permanent verwijderd.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="room-delete-cancel">Annuleer</AlertDialogCancel>
            <AlertDialogAction className="bg-[#D9534F] hover:bg-[#B23A3A]" onClick={() => doRemoveRoom(confirmRoomDel.id)} data-testid="room-delete-confirm-btn">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* v6 E8 — Per-room template change confirmation (only when room has metingen) */}
      <AlertDialog open={!!pendingRoomTplChange} onOpenChange={(v) => !v && setPendingRoomTplChange(null)}>
        <AlertDialogContent data-testid="room-template-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Template wijzigen voor "{pendingRoomTplChange?.room?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze kamer heeft al metingen. Kies hoe je verder wil:
              <br /><br />
              <strong>Alleen checklist wijzigen</strong> — bestaande metingen blijven behouden onder "Andere regels", en de nieuwe template toont enkel haar standaard-werken in de checklist.
              <br /><br />
              <strong>Vervangen door standaard</strong> — bestaande metingen worden verwijderd en de nieuwe template-defaults worden toegevoegd.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel data-testid="room-template-cancel">Annuleer</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => pendingRoomTplChange && applyRoomTemplateChange(
                pendingRoomTplChange.room.id,
                pendingRoomTplChange.newTemplateId,
                { replaceMeasurements: false },
              )}
              data-testid="room-template-keep-btn"
            >
              Alleen checklist wijzigen
            </Button>
            <AlertDialogAction
              className="bg-[#D9534F] hover:bg-[#B23A3A]"
              onClick={() => pendingRoomTplChange && applyRoomTemplateChange(
                pendingRoomTplChange.room.id,
                pendingRoomTplChange.newTemplateId,
                { replaceMeasurements: true },
              )}
              data-testid="room-template-replace-btn"
            >
              Vervangen door standaard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick room calculator dialog */}
      <Dialog open={!!quickCalcRoom} onOpenChange={(v) => !v && setQuickCalcRoom(null)}>
        <DialogContent className="max-w-md" data-testid="quick-calc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#D96C4A]" /> Snelle ruimteberekening
            </DialogTitle>
            <DialogDescription>
              {quickCalcRoom?.name} — bereken automatisch alle wanden + plafond uit L × B × H.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Lengte (m)</Label>
                <RawNumInput value={quickCalcForm.length}
                  onChange={(v) => setQuickCalcForm({ ...quickCalcForm, length: typeof v === "number" ? v : 0 })}
                  className="h-12 mt-1.5 rounded-xl font-mono" data-testid="quick-calc-length" />
              </div>
              <div>
                <Label className="text-xs">Breedte (m)</Label>
                <RawNumInput value={quickCalcForm.width}
                  onChange={(v) => setQuickCalcForm({ ...quickCalcForm, width: typeof v === "number" ? v : 0 })}
                  className="h-12 mt-1.5 rounded-xl font-mono" data-testid="quick-calc-width" />
              </div>
              <div>
                <Label className="text-xs">Hoogte (m)</Label>
                <RawNumInput value={quickCalcForm.height}
                  onChange={(v) => setQuickCalcForm({ ...quickCalcForm, height: typeof v === "number" ? v : 0 })}
                  className="h-12 mt-1.5 rounded-xl font-mono" data-testid="quick-calc-height" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center justify-between border border-[#E5E2DC] rounded-xl px-3 h-11">
                <Label className="text-sm">Wanden</Label>
                <Switch checked={quickCalcForm.walls} onCheckedChange={(v) => setQuickCalcForm({ ...quickCalcForm, walls: v })} data-testid="quick-calc-walls" />
              </div>
              <div className="flex items-center justify-between border border-[#E5E2DC] rounded-xl px-3 h-11">
                <Label className="text-sm">Plafond</Label>
                <Switch checked={quickCalcForm.ceiling} onCheckedChange={(v) => setQuickCalcForm({ ...quickCalcForm, ceiling: v })} data-testid="quick-calc-ceiling" />
              </div>
            </div>
            <div className="bg-[#F0EFEA] rounded-xl p-3 text-xs font-mono space-y-1" data-testid="quick-calc-preview">
              {quickCalcForm.walls && (
                <div>Wanden: 2×({quickCalcForm.length}×{quickCalcForm.height}) + 2×({quickCalcForm.width}×{quickCalcForm.height}) = <strong>{(2*quickCalcForm.length*quickCalcForm.height + 2*quickCalcForm.width*quickCalcForm.height).toFixed(2)} m²</strong></div>
              )}
              {quickCalcForm.ceiling && (
                <div>Plafond: {quickCalcForm.length}×{quickCalcForm.width} = <strong>{(quickCalcForm.length*quickCalcForm.width).toFixed(2)} m²</strong></div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickCalcRoom(null)} className="h-12 rounded-xl">Annuleer</Button>
            <Button onClick={applyQuickCalc} className="h-12 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C]" data-testid="quick-calc-apply">
              Lijnen toevoegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF result modal */}
      <Dialog open={!!pdfResult} onOpenChange={(v) => !v && setPdfResult(null)}>
        <DialogContent className="max-w-md" data-testid="pdf-result-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="w-5 h-5 text-[#D96C4A]" /> Offerte gegenereerd
            </DialogTitle>
            <DialogDescription>
              {pdfResult?.title || "Offerte"} · <span className="font-mono">{pdfResult?.quote_number}</span> · {pdfResult?.date}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={async () => {
                try { await openQuotePdf(pdfResult.id); }
                catch (e) {
                  const s = e?.response?.status;
                  if (s === 401) toast.error("Sessie verlopen. Log opnieuw in.");
                  else if (s === 404) toast.error("Offerte niet gevonden.");
                  else toast.error("PDF kon niet worden geopend.");
                }
              }}
              className="h-12 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] gap-2"
              data-testid="pdf-result-open"
            >
              <FileDown className="w-4 h-4" /> PDF openen
            </Button>
            <a
              href={pdfResult?.url}
              download={(pdfResult?.quote_number || "").replace(/\s+/g, "-").replace(/[^A-Za-z0-9._-]/g, "")
                ? `${(pdfResult.quote_number).replace(/\s+/g, "-").replace(/[^A-Za-z0-9._-]/g, "")}.pdf`
                : "offerte.pdf"}
              className="h-12 rounded-xl border border-[#E5E2DC] hover:bg-[#F0EFEA] flex items-center justify-center gap-2 font-medium"
              data-testid="pdf-result-download"
            >
              <FileDown className="w-4 h-4" /> Download
            </a>
            <Button
              onClick={() => sharePdf(pdfResult.url)}
              variant="outline"
              className="h-12 rounded-xl gap-2"
              data-testid="pdf-result-share"
            >
              <Send className="w-4 h-4" /> Delen
            </Button>
            <Button
              onClick={() => emailPdf(pdfResult.url)}
              variant="outline"
              className="h-12 rounded-xl gap-2"
              data-testid="pdf-result-email"
            >
              <Send className="w-4 h-4" /> E-mail
            </Button>
          </div>
          <div className="text-xs text-[#4F4F4D] border-t border-[#E5E2DC] pt-3">
            Tip: in <strong>Documenten</strong> hieronder vind je alle eerdere PDF's terug.
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-11 rounded-xl" onClick={() => setPdfResult(null)} data-testid="pdf-result-close">
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v14at — Compose-dialog voor "E-mail" actie in "Offerte gegenereerd"-modal.
          Gebruikt bestaande SMTP-endpoint /api/quotes/{id}/send-email met
          verse PDF als bijlage. Fallback: mailto: als geen mailbox gekoppeld. */}
      <QuoteEmailComposeDialog
        open={emailComposeOpen}
        onClose={() => setEmailComposeOpen(false)}
        quoteId={pdfResult?.id}
        quoteNumber={pdfResult?.quote_number}
        quoteUrl={pdfResult?.url}
        projectName={project?.name || ""}
        clientName={
          client
            ? (client.type === "private"
                ? `${client.first_name || ""} ${client.last_name || ""}`.trim()
                : (client.company_name || "")) || client.name || ""
            : ""
        }
        clientEmail={client?.email || ""}
        onQuoteStatusChange={(qs) => setProject((p) => (p ? { ...p, quote_status: qs } : p))}
      />

      {/* Sticky bottom action bar (mobile) */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-xl border-t border-[#E5E2DC] px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]" data-testid="mobile-action-bar">
        <div className="flex items-center justify-between gap-3">
          <div className="text-left flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259]">Totaal incl. BTW</div>
            <div className="font-heading font-bold text-lg text-[#2C2C2A] truncate" data-testid="mobile-total">{fmtEUR(calc.total_incl)}</div>
          </div>
          <Button onClick={save} disabled={saving} variant="outline" className="h-11 rounded-xl gap-1 px-3" data-testid="mobile-save-btn">
            <Save className="w-4 h-4" /> Opslaan
          </Button>
          <Button onClick={downloadPdf} className="h-11 rounded-xl gap-1 bg-[#D96C4A] hover:bg-[#C05A3C] px-3" data-testid="mobile-pdf-btn">
            <FileDown className="w-4 h-4" /> PDF
          </Button>
        </div>
      </div>
      <div className="lg:hidden h-20" /> {/* spacer for sticky bar */}
      {/* v14ao — AutoMeasure Details dialog (opgevoerd via zone-block Details knop). */}
      <AutoMeasureDetailsDialog
        open={autoMeasureDetailsOpen}
        onOpenChange={setAutoMeasureDetailsOpen}
        building={autoMeasureDetailsBuilding}
      />
    </div>
    </AutoSourcesContext.Provider>
  );
}

const SumRow = ({ label, value, testId }) => (
  <div className="flex items-center justify-between py-1" data-testid={testId}>
    <span className="text-white/60">{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);

